import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, HostListener, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import {
  DEFAULT_GENERATE_SETTINGS,
  DetectSurfaceItem,
  DetectSurfacesResponse,
  EstimatedSurfaceSize,
  GenerateResponse,
  GenerateSettings,
  PerspectivePoints,
  Point2D,
  ProductTexture,
  RoomSample,
  SurfaceAssignment,
  SurfaceHotspot,
  TargetSurface,
  ViewerMode,
  VisualizerUrls
} from '../../models/visualizer.models';
import { AdminApiService } from '../../services/admin-api.service';
import { LocationUsageService } from '../../services/location-usage.service';
import { Product, RoomTemplate } from '../../models/admin-api.models';
import { QuoteRequestPayload, VisualizerApiService } from '../../services/visualizer-api.service';
import { RoomVisualizerLandscapeComponent } from '../room-visualizer-landscape/room-visualizer-landscape.component';
import { ThreeRoomCanvasComponent } from '../three-room-canvas/three-room-canvas.component';

interface SurfaceOption {
  value: TargetSurface;
  label: string;
  hint: string;
}

interface SurfaceBBox {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  width?: number;
  height?: number;
  x?: number;
  y?: number;
}

interface PreparedSurfaceInstanceView {
  id: string;
  surface: TargetSurface;
  label?: string;
  wallRole?: string | null;
  maskUrl: string | null;
  sliceUrl: string | null;
  lightTextureUrl?: string | null;
  hardMaskUrl?: string | null;
  occlusionMaskUrl?: string | null;
  quad: PerspectivePoints | null;
  confidence?: number;
  source?: string | null;
  quality?: string | null;
  nonzeroPixels?: number;
  areaRatio?: number;
  bbox?: SurfaceBBox | null;
  polygon?: Point2D[];
  estimatedSize?: EstimatedSurfaceSize | null;
}

interface PreparedSurfaceView {
  surface: TargetSurface;
  available: boolean;
  reason?: string | null;
  source?: string | null;
  quality?: string | null;
  isEstimated?: boolean;
  maskUrl: string | null;
  sliceUrl: string | null;
  lightTextureUrl?: string | null;
  hardMaskUrl?: string | null;
  occlusionMaskUrl?: string | null;
  quad: PerspectivePoints | null;
  instances: PreparedSurfaceInstanceView[];
}

type MobileAdjustMode = 'main' | 'products' | 'pattern' | 'size' | 'offset' | 'rotation' | 'compare' | 'grout';
type MobileCatalogStep = 'category' | 'series' | 'product';
type CatalogUsageFilter = 'all' | 'interior' | 'exterior';
type DraggablePanel = 'catalog' | 'adjust' | 'usedProducts';
type QuoteModalState = 'closed' | 'whatsapp_form';
type VisualizerOrientationLock = 'portrait' | 'portrait-primary' | 'portrait-secondary' | 'landscape' | 'landscape-primary' | 'landscape-secondary';

interface CatalogPhotoCard {
  name: string;
  label: string;
  imageUrl: string;
  count: number;
}

interface PreviewLayerView {
  id: string;
  surface: TargetSurface;
  instanceId: string;
  label?: string | null;
  textureUrl: string;
  maskUrl: string;
  sliceUrl: string | null;
  lightTextureUrl?: string | null;
  maskFlipY?: boolean;
  wallRole?: string | null;
  perspectivePoints: PerspectivePoints | null;
  opacity: number;
  rotationAngle: number;
  textureScale: number;
  textureOffsetX: number;
  textureOffsetY: number;
  pattern: string;
  plankWidthM: number;
  plankLengthM: number;
  groutWidthM: number;
  groutEnabled: boolean;
  groutSizeMm: number;
  groutColor: string;
  roomWidthM?: number;
  roomDepthM?: number;
  surfaceWidthMm?: number | null;
  surfaceHeightMm?: number | null;
  surfaceLengthMm?: number | null;
  surfaceDepthMm?: number | null;
  productWidthMm?: number | null;
  productHeightMm?: number | null;
  pixelsPerMeter?: number;
  bbox?: SurfaceBBox | null;
  polygon?: Point2D[] | null;
}

interface QuoteProductFormItem {
  surface: string;
  productCode: string;
  productName: string;
  pattern: string;
  boxQty: number;
  area: number | null;
}

interface PatternOptionView {
  value: string;
  label: string;
  previewUrl: string;
}

@Component({
  selector: 'frantinco-room-visualizer',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, ThreeRoomCanvasComponent, RoomVisualizerLandscapeComponent],
  templateUrl: './room-visualizer.component.html',
  styleUrl: './room-visualizer.component.scss'
})
export class RoomVisualizerComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly api = inject(VisualizerApiService);
  private readonly adminApi = inject(AdminApiService);
  private readonly locationUsage = inject(LocationUsageService);
  private readonly objectUrls = new Set<string>();
  readonly fallbackProductImageUrl = this.api.resolveAssetUrl('/uploads/others/no-image.png');
  companyPreviewImageUrl = this.fallbackProductImageUrl;

  @ViewChild(ThreeRoomCanvasComponent) private roomCanvas?: ThreeRoomCanvasComponent;


  private readonly surfaceOrder: TargetSurface[] = [
    'ceiling',
    'wall',
    'floor'
  ];

  private readonly hotspotPositions: Partial<Record<TargetSurface, { xRatio: number; yRatio: number }>> = {
    ceiling: { xRatio: 0.56, yRatio: 0.11 },
    wall: { xRatio: 0.23, yRatio: 0.43 },
    floor: { xRatio: 0.55, yRatio: 0.80 }
  };

  readonly surfaceOptions: SurfaceOption[] = [
    { value: 'floor', label: 'Lantai', hint: 'Area lantai terdeteksi AI' },
    { value: 'wall', label: 'Dinding', hint: 'Area dinding terdeteksi AI' },
    { value: 'ceiling', label: 'Ceiling', hint: 'Area Ceiling terdeteksi AI' }
  ];
  readonly groutSizeOptions = [2, 3, 5, 8, 10];

  readonly roomGroups = [
    'all',
    'bathroom',
    'bedroom',
    'office',
    'entrance',
    'basement',
    'hall',
    'healthcare',
    'childrenroom',
    'kitchen',
    'livingroom',
    'other',
    'staircase'
  ];
  readonly roomGroupLabels: Record<string, string> = {
    all: 'Semua',
    office: 'Kantor',
    bathroom: 'Kamar Mandi',
    bedroom: 'Kamar Tidur',
    entrance: 'Area Masuk',
    basement: 'Basement',
    hall: 'Hall',
    kitchen: 'Dapur',
    livingroom: 'Ruang Keluarga',
    healthcare: 'Fasilitas Kesehatan',
    childrenroom: 'Kamar Anak',
    staircase: 'Tangga',
    other: 'Lainnya'
  };

  sampleRooms: RoomSample[] = [];
  products: ProductTexture[] = [];
  selectedProduct: ProductTexture | null = null;
  selectedProductTextureFile: File | null = null;
  selectedCategory = 'all';
  selectedSeries = 'all';
  selectedRoomGroup = 'all';
  pendingRoomGroup = this.selectedRoomGroup;
  sampleColumnCount = 5;
  sampleRoomColumns: RoomSample[][] = [];
  roomFilterOpen = false;
  selectedRoomFile: File | null = null;
  selectedRoomUrl: string | null = null;
  activeRoomSource: 'upload' | 'template' | null = null;
  selectedTemplateId: string | null = null;
  surfaceAssignments: Record<string, SurfaceAssignment | undefined> = {};

  detectedSurfaceHotspots: SurfaceHotspot[] = [];
  detectedSurfaces: DetectSurfaceItem[] = [];
  isDetectingSurfaces = false;
  isPreparingSurface = false;

  apiBaseUrl = this.api.getBaseUrl();
  apiStatus = 'Belum dicek';
  isGenerating = false;
  errorMessage = '';
  successMessage = '';
  advancedOpen = false;
  viewerMode: ViewerMode = 'original';
  comparePosition = 0.5;
  catalogOpen = true;
  adjustmentOpen = false;
  usedProductsOpen = false;
  mobileAdjustMode: MobileAdjustMode = 'main';
  mobileCatalogStep: MobileCatalogStep = 'category';
  mobileCompareControlsOpen = false;
  catalogSearchKeyword = '';
  catalogFilterOpen = false;
  catalogUsageFilter: CatalogUsageFilter = 'all';
  catalogDrawerOffsetX = 0;
  catalogDrawerOffsetY = 0;
  catalogDrawerDragging = false;
  adjustDrawerOffsetX = 0;
  adjustDrawerOffsetY = 0;
  adjustDrawerDragging = false;
  usedProductsOffsetX = 0;
  usedProductsOffsetY = 0;
  usedProductsDragging = false;
  private activeDragPanel: DraggablePanel | null = null;
  private drawerDragStartX = 0;
  private drawerDragStartY = 0;
  private drawerStartOffsetX = 0;
  private drawerStartOffsetY = 0;

  settings: GenerateSettings = { ...DEFAULT_GENERATE_SETTINGS };
  lastResponse: GenerateResponse | null = null;
  urls: VisualizerUrls = {
    original: null,
    texture: null,
    result: null,
    mask: null,
    slice: null
  };
  preparedImageId: string | null = null;
  preparedFloorQuad: PerspectivePoints | null = null;
  preparedSurfaces: Partial<Record<TargetSurface, PreparedSurfaceView>> = {};
  previewLayers: PreviewLayerView[] = [];
  activeInstanceId: string | null = null;
  patternDropdownOpen = false;
  private lastRandomPatternSelectAt = 0;
  private lastMixPatternSelectAt = 0;
  quoteModalState: QuoteModalState = 'closed';
  quoteProducts: QuoteProductFormItem[] = [];
  quoteNote = '';
  quoteError = '';
  quoteSubmitting = false;
  private productsLoaded = false;
  private pendingEstimatorReturnPayload: string | null = null;
  

  ngOnInit(): void {
    this.updateSampleColumnCount();
    this.loadCompanyPreviewImage();
    this.loadRoomTemplatesFromDatabase();
    this.loadProductsFromDatabase();
    this.restoreFromEstimatorReturnPayload();
  }

  ngOnDestroy(): void {
    for (const url of this.objectUrls) {
      URL.revokeObjectURL(url);
    }
  }

  get filteredRooms(): RoomSample[] {
    if (this.selectedRoomGroup === 'all') {
      return this.sampleRooms;
    }

    return this.sampleRooms.filter((room) => room.group === this.selectedRoomGroup);
  }

  rebuildSampleRoomColumns(): void {
    const columnCount = Math.max(1, this.sampleColumnCount);
    const columns = Array.from({ length: columnCount }, () => [] as RoomSample[]);

    this.filteredRooms.forEach((room, index) => {
      columns[index % columnCount].push(room);
    });

    this.sampleRoomColumns = columns.filter((column) => column.length > 0);
  }

  sampleCardHeight(columnIndex: number, rowIndex: number): number {
    const desktopPattern = [250, 320, 250, 220, 250, 320, 280, 240];
    const mobilePattern = [142, 180, 162, 132, 172, 148];
    const pattern = this.sampleColumnCount <= 2 ? mobilePattern : desktopPattern;
    const patternIndex = (rowIndex * this.sampleColumnCount + columnIndex) % pattern.length;

    return pattern[patternIndex];
  }

  floorIndex(value: number): number {
    return Math.floor(value);
  }

  roomGroupLabel(group: string): string {
    return this.roomGroupLabels[group] || group;
  }

  roomGroupCount(group: string): number {
    if (group === 'all') {
      return this.sampleRooms.length;
    }

    return this.sampleRooms.filter((room) => room.group === group).length;
  }

  selectRoomGroup(group: string): void {
    this.selectedRoomGroup = group;
    this.pendingRoomGroup = group;
    this.rebuildSampleRoomColumns();
  }

  trackBySampleColumn(index: number): number {
    return index;
  }

  trackBySampleRoom(index: number, room: RoomSample): string {
    return String(room.id || index);
  }

  trackByCatalogPhotoCard(index: number, card: CatalogPhotoCard): string {
    return `${card.name || card.label || 'catalog'}-${index}`;
  }

  trackByProductTexture(index: number, product: ProductTexture): string {
    return product.sku || product.name || String(index);
  }

  openRoomFilter(): void {
    this.pendingRoomGroup = this.selectedRoomGroup;
    this.roomFilterOpen = true;
  }

  closeRoomFilter(): void {
    this.roomFilterOpen = false;
  }

  choosePendingRoomGroup(group: string): void {
    this.pendingRoomGroup = group;
  }

  applyRoomFilter(): void {
    this.selectedRoomGroup = this.pendingRoomGroup;
    this.roomFilterOpen = false;
  }

  get categoryCards(): CatalogPhotoCard[] {
    if (!this.canShowCatalogProducts) {
      return [];
    }

    const compatible = this.filterProductsByUsage(
      this.compatibleProductsForSurface(this.settings.targetClass)
    );

    const cards = new Map<string, CatalogPhotoCard>();

    for (const product of compatible) {
      const rawProduct = product as any;
      const categoryName = product.category || rawProduct.category_name || 'Lainnya';
      const categoryImage = this.safeProductImageUrl(
        rawProduct.categoryImage ||
        rawProduct.category_image ||
        product.thumbnailUrl ||
        this.fallbackProductImageUrl
      );
      const categoryKey = this.normalizeCatalogCategory(categoryName);

      const existing = cards.get(categoryKey);

      if (existing) {
        existing.count += 1;

        if (existing.imageUrl === this.fallbackProductImageUrl && categoryImage !== this.fallbackProductImageUrl) {
          existing.imageUrl = categoryImage;
        }

        continue;
      }

      cards.set(categoryKey, {
        name: categoryName,
        label: categoryName,
        imageUrl: categoryImage,
        count: 1
      });
    }

    return Array.from(cards.values());
  }

  get seriesCards(): CatalogPhotoCard[] {
    if (!this.canShowCatalogProducts) {
      return [];
    }

    const selectedCategoryKey = this.normalizeCatalogCategory(this.selectedCategory);
    const compatible = this.filterProductsByUsage(
      this.compatibleProductsForSurface(this.settings.targetClass)
    ).filter((product) => {
      const productCategoryKey = this.normalizeCatalogCategory(product.category);

      return (
        !selectedCategoryKey || selectedCategoryKey === 'all' || productCategoryKey === selectedCategoryKey
      );
    });

    const cards = new Map<string, CatalogPhotoCard>();

    for (const product of compatible) {
      const rawProduct = product as any;
      const seriesName = rawProduct.seriesName || rawProduct.series_name || rawProduct.series || 'Tanpa Seri';
      const seriesImage = this.safeProductImageUrl(
        rawProduct.seriesImage ||
        rawProduct.series_image ||
        rawProduct.categoryImage ||
        rawProduct.category_image ||
        product.thumbnailUrl ||
        this.fallbackProductImageUrl
      );
      const seriesKey = this.normalizeCatalogCategory(seriesName);

      const existing = cards.get(seriesKey);

      if (existing) {
        existing.count += 1;

        if (existing.imageUrl === this.fallbackProductImageUrl && seriesImage !== this.fallbackProductImageUrl) {
          existing.imageUrl = seriesImage;
        }

        continue;
      }

      cards.set(seriesKey, {
        name: seriesName,
        label: seriesName,
        imageUrl: seriesImage,
        count: 1
      });
    }

    return Array.from(cards.values());
  }

  get filteredProducts(): ProductTexture[] {
    if (!this.canShowCatalogProducts) {
      return [];
    }

    const compatible = this.filterProductsByUsage(
      this.compatibleProductsForSurface(this.settings.targetClass)
    );
    const selectedCategoryKey = this.normalizeCatalogCategory(this.selectedCategory);
    const selectedSeriesKey = this.normalizeCatalogCategory(this.selectedSeries);

    return compatible.filter((product) => {
      const rawProduct = product as any;
      const categoryMatches = !selectedCategoryKey || selectedCategoryKey === 'all' ||
        this.normalizeCatalogCategory(product.category) === selectedCategoryKey;
      const seriesName = rawProduct.seriesName || rawProduct.series_name || rawProduct.series || '';
      const seriesMatches = !selectedSeriesKey || selectedSeriesKey === 'all' ||
        this.normalizeCatalogCategory(seriesName) === selectedSeriesKey;

      return categoryMatches && seriesMatches;
    });
  }


  get filteredCategoryCards(): CatalogPhotoCard[] {
    return this.filterCatalogCards(this.categoryCards);
  }

  get filteredSeriesCards(): CatalogPhotoCard[] {
    return this.filterCatalogCards(this.seriesCards);
  }

  get searchedProducts(): ProductTexture[] {
    const term = this.normalizedCatalogSearchKeyword;

    if (!term) {
      return this.filteredProducts;
    }

    return this.filteredProducts.filter((product) => this.productMatchesCatalogSearch(product, term));
  }

  get hasCatalogUsageFilter(): boolean {
    return this.catalogUsageFilter !== 'all';
  }

  get catalogUsageFilterLabel(): string {
    const labels: Record<CatalogUsageFilter, string> = {
      all: 'Semua',
      interior: 'Interior',
      exterior: 'Exterior'
    };

    return labels[this.catalogUsageFilter];
  }

  toggleCatalogFilter(): void {
    this.catalogFilterOpen = !this.catalogFilterOpen;
  }

  selectCatalogUsageFilter(filter: CatalogUsageFilter): void {
    this.catalogUsageFilter = filter;
    this.catalogFilterOpen = false;
  }

  catalogUsageFilterCount(filter: CatalogUsageFilter): number {
    const compatible = this.compatibleProductsForSurface(this.settings.targetClass);

    if (filter === 'all') {
      return compatible.length;
    }

    return compatible.filter((product) => this.productMatchesUsageFilter(product, filter)).length;
  }

  private filterProductsByUsage(products: ProductTexture[]): ProductTexture[] {
    const usageFilter = this.catalogUsageFilter;

    if (usageFilter === 'all') {
      return products;
    }

    return products.filter((product) => this.productMatchesUsageFilter(product, usageFilter));
  }

  private productMatchesUsageFilter(product: ProductTexture, filter: Exclude<CatalogUsageFilter, 'all'>): boolean {
    return this.productUsageFlag(product as any, filter) === 1;
  }

  private get normalizedCatalogSearchKeyword(): string {
    return this.normalizeCatalogCategory(this.catalogSearchKeyword);
  }

  private filterCatalogCards(cards: CatalogPhotoCard[]): CatalogPhotoCard[] {
    const term = this.normalizedCatalogSearchKeyword;

    if (!term) {
      return cards;
    }

    return cards.filter((card) =>
      this.normalizeCatalogCategory(`${card.label} ${card.name}`).includes(term)
    );
  }

  private productMatchesCatalogSearch(product: ProductTexture, term: string): boolean {
    const rawProduct = product as any;
    const searchable = [
      product.sku,
      rawProduct.code,
      product.name,
      product.description,
      product.category,
      rawProduct.category_name,
      rawProduct.series,
      rawProduct.seriesName,
      rawProduct.series_name,
      rawProduct.type,
      rawProduct.thickness,
      rawProduct.sizeType,
      rawProduct.size_type
    ]
      .filter((item) => item !== undefined && item !== null)
      .join(' ');

    return this.normalizeCatalogCategory(searchable).includes(term);
  }

  get mobileSelectedCategoryLabel(): string {
    const selectedCategoryKey = this.normalizeCatalogCategory(this.selectedCategory);
    return this.categoryCards.find((category) => this.normalizeCatalogCategory(category.name) === selectedCategoryKey)?.label ?? 'Produk';
  }

  get mobileSelectedSeriesLabel(): string {
    const selectedSeriesKey = this.normalizeCatalogCategory(this.selectedSeries);
    return this.seriesCards.find((series) => this.normalizeCatalogCategory(series.name) === selectedSeriesKey)?.label ?? 'Seri';
  }

  get mobileCatalogTitle(): string {
    if (this.mobileCatalogStep === 'category') {
      return 'Pilih kategori';
    }

    if (this.mobileCatalogStep === 'series') {
      return 'Pilih product series';
    }

    return 'Pilih produk';
  }

  get activeRoomTitle(): string {
    if (this.activeRoomSource === 'template' && this.selectedTemplateId) {
      const selectedTemplate = this.sampleRooms.find((room) => String(room.id) === String(this.selectedTemplateId));
      return selectedTemplate?.title || `Template Ruangan #${this.selectedTemplateId}`;
    }

    return this.selectedRoomFile?.name || 'Foto Ruangan';
  }

  get activeRoomSummary(): string {
    const surfaceCount = this.availableSurfaceOptions.length || this.detectedSurfaceHotspots.length || 0;
    const sourceLabel = this.activeRoomSource === 'template' ? 'Template ruang' : 'Upload manual';
    return `${surfaceCount} area tersedia - ${sourceLabel}`;
  }

  normalizeCatalogCategory(categoryName?: string | null): string {
    return String(categoryName || '')
      .trim()
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  }

  isSelectedCategory(categoryName?: string | null): boolean {
    return this.normalizeCatalogCategory(this.selectedCategory) === this.normalizeCatalogCategory(categoryName);
  }

  get canGenerate(): boolean {
    return (this.activeRoomSource === 'template' || !!this.selectedRoomFile) && this.configuredSurfaceCount > 0 && !this.isGenerating;
  }
  get isCanvasProcessing(): boolean {
    return this.isPreparingSurface || this.isDetectingSurfaces || this.isGenerating;
  }

  get canvasLoadingText(): string {
    if (this.isGenerating) {
      return `Memproses ${this.configuredSurfaceCount} area...`;
    }

    if (this.isPreparingSurface) {
      return 'Menyiapkan mask dan slice area...';
    }

    if (this.isDetectingSurfaces) {
      return 'Mendeteksi area ruangan...';
    }

    return '';
  }
  get configuredSurfaceCount(): number {
    return this.getOrderedAssignments().length;
  }

  get usedProductCount(): number {
    return this.assignedSurfaceList.length;
  }

  get selectedSurfaceLabel(): string {
    const activeHotspot = this.detectedSurfaceHotspots.find(
      (hotspot) => hotspot.surface === this.settings.targetClass && (hotspot.instanceId || null) === (this.activeInstanceId || null)
    );

    if (activeHotspot?.label) {
      return activeHotspot.label;
    }

    return this.surfaceLabel(this.settings.targetClass);
  }

  get activeSurfaceForCanvas(): TargetSurface {
    return this.settings.targetClass || 'floor';
  }


  get catalogDrawerTransform(): string | null {
    if (!this.catalogOpen || !this.isDesktopCatalogViewport()) {
      return null;
    }

    return `translate(${this.catalogDrawerOffsetX}px, ${this.catalogDrawerOffsetY}px) scale(1)`;
  }

  get adjustDrawerTransform(): string | null {
    if (!this.adjustmentOpen || !this.activeProduct || !this.isDesktopCatalogViewport()) {
      return null;
    }

    return `translate(${this.adjustDrawerOffsetX}px, ${this.adjustDrawerOffsetY}px) scale(1)`;
  }

  get usedProductsTransform(): string | null {
    if (!this.usedProductsOpen || this.usedProductCount === 0 || !this.isDesktopCatalogViewport()) {
      return null;
    }

    return `translate(${this.usedProductsOffsetX}px, ${this.usedProductsOffsetY}px)`;
  }

  get canOpenCatalog(): boolean {
    if (!this.urls.original || this.isDetectingSurfaces || this.isPreparingSurface) {
      return false;
    }

    const hasPreparedSurfaces = Object.keys(this.preparedSurfaces).length > 0;
    if (!hasPreparedSurfaces) {
      return true;
    }

    const prepared = this.preparedSurfaces[this.settings.targetClass] ?? null;
    const assignment = this.activeSurfaceAssignment;
    return !!assignment || !!(prepared && prepared.available);
  }


  get canShowCatalogProducts(): boolean {
    if (!this.urls.original || this.isDetectingSurfaces || this.isPreparingSurface) {
      return false;
    }

    const hasPreparedSurfaces = Object.keys(this.preparedSurfaces).length > 0;
    if (!hasPreparedSurfaces) {
      return true;
    }

    const prepared = this.preparedSurfaces[this.settings.targetClass] ?? null;
    const assignment = this.activeSurfaceAssignment;
    return !!assignment || !!(prepared && prepared.available);
  }

  get availableSurfaceOptions(): SurfaceOption[] {
    const hasPreparedSurfaces = Object.keys(this.preparedSurfaces).length > 0;

    if (!hasPreparedSurfaces) {
      return this.surfaceOptions;
    }

    return this.surfaceOptions.filter((option) => {
      const prepared = this.preparedSurfaces[option.value] ?? null;
      const assignment = this.assignmentFor(option.value);
      return !!assignment || !!(prepared && prepared.available);
    });
  }

  get activeSurfaceAssignment(): SurfaceAssignment | null {
    const exact = this.surfaceAssignments[this.activeAssignmentKey()] ?? null;
    if (exact || !this.isMultiInstanceSurface(this.settings.targetClass) || !this.activeInstanceId) {
      return exact;
    }

    return this.surfaceAssignments[this.assignmentKey(this.settings.targetClass, null)] ?? null;
  }

  get activeProduct(): ProductTexture | null {
    return this.activeSurfaceAssignment?.product ?? this.selectedProduct;
  }

  get selectedProductTextureUrl(): string | null {
    const textureUrl = (
      this.activeSurfaceAssignment?.textureUrl ||
      this.urls.texture ||
      this.selectedProduct?.textureUrl ||
      null
    );

    return textureUrl ? this.safeProductImageUrl(textureUrl) : null;
  }

  get currentSurfaceHint(): string {
    return this.surfaceOptions.find((option) => option.value === this.settings.targetClass)?.hint ?? '';
  }

  get selectedAssignmentLabel(): string {
    const assignment = this.activeSurfaceAssignment;
    return assignment ? `${assignment.product.sku} · ${assignment.product.name}` : 'Belum ada produk untuk titik ini.';
  }

  get activePreparedInstance(): PreparedSurfaceInstanceView | null {
    const prepared = this.preparedSurfaces[this.settings.targetClass] ?? null;

    if (!prepared?.instances?.length) {
      return null;
    }

    if (this.activeInstanceId) {
      return prepared.instances.find((item) => item.id === this.activeInstanceId) ?? null;
    }

    return prepared.instances[0] ?? null;
  }

  get selectedSurfaceSizeLabel(): string {
    const size = this.activePreparedInstance?.estimatedSize || null;

    if (!size) {
      return 'Ukuran belum tersedia';
    }

    const width = this.formatMm(size.width_mm);
    const heightOrLength = this.settings.targetClass === 'wall'
      ? this.formatMm(size.height_mm)
      : this.formatMm(size.length_mm || size.depth_mm);

    return `${width} x ${heightOrLength} mm · ${size.scale_mode || '-'} · ${size.confidence || '-'}`;
  }

  get mobileAdjustTitle(): string {
    const suffix: Record<MobileAdjustMode, string> = {
      main: '',
      products: ' - Produk',
      pattern: ' - Pola',
      size: ' - Ukuran',
      offset: ' - Pindahkan pola',
      rotation: ' - Rotasi',
      compare: ' - Komparasi',
      grout: ' - Nat'
    };

    return `Menyesuaikan${suffix[this.mobileAdjustMode]}`;
  }

  get landscapeAdjustTitle(): string {
    const labels: Record<MobileAdjustMode, string> = {
      main: 'Atur',
      products: 'Produk',
      pattern: 'Pola',
      size: 'Ukuran',
      offset: 'Pindah',
      rotation: 'Rotasi',
      compare: 'Komparasi',
      grout: 'Nat'
    };

    return labels[this.mobileAdjustMode];
  }

  get currentGroutSizeMm(): number {
    const size = Number(this.settings.groutSizeMm || (this.settings.groutWidthM || 0) * 1000 || 5);
    const normalizedSize = Number.isFinite(size) && size > 0 ? Math.round(size) : 5;

    return this.groutSizeOptions.includes(normalizedSize) ? normalizedSize : 5;
  }

  get currentGroutColor(): string {
    return this.settings.groutColor || this.activeProduct?.groutColor || '#9E9E9E';
  }

  get activePatternOptions(): PatternOptionView[] {
    const rawPatterns = this.activeProduct?.availablePatterns?.length
      ? this.activeProduct.availablePatterns
      : ['grid', 'brick', 'brick_1_3', 'random'];
    const uniquePatterns = new Set<string>();

    for (const pattern of rawPatterns) {
      uniquePatterns.add(this.normalizePatternName(pattern));
    }

    uniquePatterns.add(this.normalizePatternName(this.settings.pattern));

    return Array.from(uniquePatterns).map((value) => ({
      value,
      label: this.patternDisplayLabel(value),
      previewUrl: this.patternPreviewUrl(value)
    }));
  }

  get selectedPatternOption(): PatternOptionView {
    const selectedPattern = this.normalizePatternName(this.settings.pattern);

    return this.activePatternOptions.find((option) => option.value === selectedPattern) || {
      value: selectedPattern,
      label: this.patternDisplayLabel(selectedPattern),
      previewUrl: this.patternPreviewUrl(selectedPattern)
    };
  }

  get surfaceHotspots(): SurfaceHotspot[] {
    const baseHotspots = this.detectedSurfaceHotspots.length > 0
      ? this.detectedSurfaceHotspots
      : this.surfaceOptions.map((option) => {
          const position = this.hotspotPositions[option.value] ?? { xRatio: 0.5, yRatio: 0.5 };
          return {
            surface: option.value,
            label: option.label,
            hint: option.hint,
            xRatio: position.xRatio,
            yRatio: position.yRatio,
            productSku: null,
            productName: null,
            productThumbnailUrl: null,
            isConfigured: false
          } as SurfaceHotspot;
        });

    const wallLabelByKey = new Map<string, string>();
    baseHotspots
      .filter((hotspot) => hotspot.surface === 'wall')
      .slice()
      .sort((left, right) => (left.xRatio || 0) - (right.xRatio || 0))
      .forEach((hotspot, index) => {
        wallLabelByKey.set(this.hotspotKey(hotspot), `Dinding ${index + 1}`);
      });

    return baseHotspots.map((hotspot) => {
      const assignment =
        this.surfaceAssignments[this.assignmentKey(hotspot.surface, hotspot.instanceId)] ??
        (hotspot.surface === 'wall'
          ? this.surfaceAssignments[this.assignmentKey('wall', null)]
          : null);
      return {
        ...hotspot,
        label: wallLabelByKey.get(this.hotspotKey(hotspot)) || hotspot.label,
        productSku: assignment?.product.sku ?? null,
        productName: assignment?.product.name ?? null,
        productThumbnailUrl: assignment?.product.thumbnailUrl ?? null,
        isConfigured: !!assignment
      };
    });
  }

  get assignedSurfaceList(): SurfaceAssignment[] {
    return this.getOrderedAssignments();
  }

  usedAssignmentLabel(assignment: SurfaceAssignment): string {
    const hotspot = this.detectedSurfaceHotspots.find(
      (item) => item.surface === assignment.surface && (item.instanceId || null) === (assignment.instanceId || null)
    );

    return hotspot?.label || this.surfaceLabel(assignment.surface);
  }

  isUsedAssignmentActive(assignment: SurfaceAssignment): boolean {
    return assignment.surface === this.settings.targetClass &&
      (assignment.instanceId || null) === (this.activeInstanceId || null);
  }

  private loadRoomTemplatesFromDatabase(): void {
    this.adminApi.getRoomTemplates(true).subscribe({
      next: (templates: RoomTemplate[]) => {
        this.sampleRooms = templates
          .filter((item) => this.isTruthy(item.is_active ?? 1))
          .map((item) => {
            const title = String(item.title || item.name || `Template Ruangan #${item.id}`).trim();
            return {
              id: String(item.id),
              title,
              group: item.room_group,
              imageUrl: this.normalizeImageUrl(item.image_url || item.imageUrl),
              processStatus: item.process_status || 'draft',
              processDone: item.process_done ?? 0,
              templateDir: item.template_dir || null,
              manifestUrl: item.manifest_url || null,
              canVisualize: item.can_visualize ?? 0
            };
          });
        this.rebuildSampleRoomColumns();
      },
      error: (error) => {
        this.showError(error.error?.message || 'Gagal mengambil foto template dari database.');
      }
    });
  }

  private loadProductsFromDatabase(): void {
    this.adminApi.getActiveProducts().subscribe({
      next: (products: Product[]) => {
        const activeProducts = this.uniqueProducts(products)
          .filter((item) => this.isProductVisibleInVisualizer(item));

        this.products = activeProducts.flatMap((item) => this.mapProductToTextures(item));
        this.productsLoaded = true;
        this.restoreFromEstimatorReturnPayload();
        this.ensureSelectedProductForActiveSurface();

        if (this.products.length === 0) {
          this.showError('Belum ada produk active yang bisa ditampilkan di visualizer.');
        }
      },
      error: (error) => {
        this.productsLoaded = true;
        this.restoreFromEstimatorReturnPayload();
        this.showError(error.error?.message || 'Gagal mengambil produk dari database.');
      }
    });
  }

private parseJsonArray<T>(value: unknown, fallback: T[]): T[] {
  if (Array.isArray(value)) {
    return value as T[];
  }

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);

      if (Array.isArray(parsed)) {
        return parsed as T[];
      }
    } catch {
      return fallback;
    }
  }

  return fallback;
}

private normalizePatternName(pattern: unknown): string {
  const value = String(pattern || '').trim().toLowerCase();

  const alias: Record<string, string> = {
    bata: 'brick',
    plank: 'grid',
    straight_plank: 'grid',
    straight: 'grid',
    brick: 'brick',
    offset: 'brick',
    third: 'brick_1_3',
    brick_1_3: 'brick_1_3',
    'brick-1-3': 'brick_1_3',
    'brick 1/3': 'brick_1_3',
    '1/3': 'brick_1_3',
    mix: 'mix',
    mixed: 'mix',
    random: 'random',
    acak: 'random',
    grid: 'grid',
    tile: 'grid',
    chessboard: 'chessboard',
    checkerboard: 'chessboard',
    herringbone: 'herringbone',
    chevron: 'chevron',
    basket: 'basketweave',
    basketweave: 'basketweave'
  };

  return alias[value] || value || 'grid';
}

private patternDisplayLabel(pattern: unknown): string {
  const patternValue = this.normalizePatternName(pattern);
  const labels: Record<string, string> = {
    random: 'Acak',
    grid: 'Grid',
    brick: 'Brick',
    brick_1_3: 'Bata 1/3',
    mix: 'Mix',
    chessboard: 'Chessboard',
    herringbone: 'Herringbone',
    chevron: 'Chevron',
    basketweave: 'Basketweave'
  };

  return labels[patternValue] || patternValue;
}

patternPreviewUrl(pattern: unknown): string {
  const patternValue = this.normalizePatternName(pattern);
  const filenames: Record<string, string> = {
    brick: 'brick.png',
    brick_1_3: 'brick_1_3.png',
    random: 'random.png',
    grid: 'grid.png',
    herringbone: 'herringbone.png',
    chevron: 'chevron.png',
    basketweave: 'basket.png',
    mix: 'mix.png',
    chessboard: 'chessboard.png'
  };

  return `assets/visualizer/pattern-previews/${filenames[patternValue] || 'grid.png'}`;
}

private mapProductToTextures(product: Product): ProductTexture[] {
  const raw = product as any;
  const surfaces = this.getProductSurfaces(product);

  const imageUrl = this.normalizeImageUrl(
    raw.image_url ||
    raw.imageUrl ||
    raw.imageHdUrl ||
    raw.image_hd_url ||
    raw.textureUrl ||
    raw.image
  );

  const textureUrl = this.normalizeImageUrl(
    raw.texture_hd_url ||
    raw.textureHdUrl ||
    raw.texture_url ||
    raw.textureUrl ||
    raw.image_hd_url ||
    raw.imageHdUrl ||
    raw.image_url ||
    raw.imageUrl ||
    raw.image
  );

  const widthMm = Number(raw.width || raw.series?.descproducts?.[0]?.width || 0);
  const heightMm = Number(raw.height || raw.series?.descproducts?.[0]?.height || 0);

  const plankWidthM = Number(
    raw.plankWidthM ||
    raw.plank_width_m ||
    (widthMm ? widthMm / 1000 : 0.184)
  );

  const plankLengthM = Number(
    raw.plankLengthM ||
    raw.plank_length_m ||
    (heightMm ? heightMm / 1000 : 1.22)
  );

  const availablePatterns = this.parseJsonArray<string>(
    raw.availablePatterns || raw.available_patterns,
    ['grid', 'brick', 'brick_1_3', 'random']
  )
    .map((item) => this.normalizePatternName(item))
    .filter(Boolean);

  const defaultPattern = this.normalizePatternName(
    raw.defaultPattern ||
    raw.default_pattern ||
    availablePatterns[0] ||
    'grid'
  );

  const textureRepeatScale = Number(
    raw.textureRepeatScale ??
    raw.texture_repeat_scale ??
    1
  );

  const defaultRotation = Number(
    raw.defaultRotation ??
    raw.default_rotation ??
    0
  );

  const defaultOffsetX = Number(
    raw.defaultOffsetX ??
    raw.default_offset_x ??
    0
  );

  const defaultOffsetY = Number(
    raw.defaultOffsetY ??
    raw.default_offset_y ??
    0
  );


  const categoryName =
    raw.category_name ||
    raw.category?.name ||
    raw.series?.category?.name ||
    'Uncategorized';

  const seriesName =
    raw.series_name ||
    raw.series?.name ||
    '';

  const categoryImageUrl = this.normalizeImageUrl(
    raw.category_image ||
    raw.categoryImage ||
    raw.category?.image ||
    raw.series?.category?.image ||
    null
  );

  const seriesImageUrl = this.normalizeImageUrl(
    raw.series_image ||
    raw.seriesImage ||
    raw.series?.image ||
    null
  );

  const groutSizes = this.parseJsonArray<number | string>(
    raw.groutSizes || raw.grout_sizes,
    this.groutSizeOptions
  )
    .map((item) => Number(item))
    .filter((item) => Number.isFinite(item) && item > 0);

  const rawDefaultGroutSize = Number(
    raw.defaultGroutSize ??
    raw.default_grout_size ??
    5
  );
  const defaultGroutSize = this.groutSizeOptions.includes(rawDefaultGroutSize)
    ? rawDefaultGroutSize
    : 5;

  return surfaces.map((surface) => ({
    id: raw.id,
    sku: raw.code || raw.sku || `PRD-${raw.id}-${surface}`,
    code: raw.code || raw.sku || `PRD-${raw.id}`,
    name: raw.name || raw.product_name || 'Produk',
    description: raw.description || (raw.category_name ? `Produk kategori ${raw.category_name}` : 'Produk dari database'),
    category: categoryName,
    category_name: categoryName,
    categoryImage: this.safeProductImageUrl(categoryImageUrl),
    category_image: this.safeProductImageUrl(categoryImageUrl),
    series: seriesName || 'Tanpa Seri',
    seriesName: seriesName || 'Tanpa Seri',
    series_name: seriesName || 'Tanpa Seri',
    seriesImage: this.safeProductImageUrl(seriesImageUrl),
    series_image: this.safeProductImageUrl(seriesImageUrl),
    targetClass: surface,
    thumbnailUrl: this.safeProductImageUrl(imageUrl),
    textureUrl: this.safeProductImageUrl(textureUrl),

    plankWidthM,
    plankLengthM,

    coverage: Number(raw.coverage || raw.series?.descproducts?.[0]?.coverage || 0),
    coverageType: raw.coverageType || raw.coverage_type || raw.series?.descproducts?.[0]?.coverageType || 'Box',
    waste: Number(raw.waste || raw.series?.descproducts?.[0]?.waste || 0),
    interior: this.productUsageFlag(raw, 'interior'),
    exterior: this.productUsageFlag(raw, 'exterior'),

    width: widthMm,
    height: heightMm,
    sizeType: raw.sizeType || raw.size_type || raw.series?.descproducts?.[0]?.sizeType || 'mm',
    type: raw.productType || raw.product_type || surface,
    thickness: raw.thickness || '-',

    availablePatterns: availablePatterns.length > 0 ? availablePatterns : ['grid', 'brick', 'brick_1_3', 'random'],
    groutSizes: groutSizes.length > 0 ? groutSizes : this.groutSizeOptions,
    defaultPattern,
    textureRepeatScale: Number.isFinite(textureRepeatScale) && textureRepeatScale > 0 ? textureRepeatScale : 1,
    defaultRotation: Number.isFinite(defaultRotation) ? defaultRotation : 0,
    defaultOffsetX: Number.isFinite(defaultOffsetX) ? defaultOffsetX : 0,
    defaultOffsetY: Number.isFinite(defaultOffsetY) ? defaultOffsetY : 0,
    defaultGroutSize: Number.isFinite(defaultGroutSize) && defaultGroutSize > 0 ? defaultGroutSize : 5,
    groutColor: raw.groutColor || raw.grout_color || '#9E9E9E'
  } as ProductTexture));
}

  private getProductSurfaces(product: Product): TargetSurface[] {
    const raw = product as any;
    const surfaces: TargetSurface[] = [];
    const flags: TargetSurface[] = ['floor', 'wall', 'ceiling'];

    for (const surface of flags) {
      if (this.isTruthy(raw[surface])) {
        surfaces.push(surface);
      }
    }

    const categoryText = String(raw.category_name || raw.category?.name || raw.name || '').toLowerCase();
    if (surfaces.length === 0) {
      if (categoryText.includes('floor') || categoryText.includes('spc') || categoryText.includes('lantai')) {
        surfaces.push('floor');
      } else if (categoryText.includes('counter') || categoryText.includes('table') || categoryText.includes('meja')) {
        surfaces.push('countertop');
      } else if (categoryText.includes('ceiling') || categoryText.includes('plafon')) {
        surfaces.push('ceiling');
      } else if (categoryText.includes('backsplash')) {
        surfaces.push('backsplash');
      } else {
        surfaces.push('wall');
      }
    }

    return surfaces;
  }

  private isTruthy(value: unknown): boolean {
    return value === true || value === 1 || value === '1' || value === 'true';
  }

  private productUsageFlag(raw: any, filter: Exclude<CatalogUsageFilter, 'all'>): number {
    const aliases = filter === 'interior'
      ? ['interior', 'is_interior', 'indoor', 'is_indoor']
      : ['exterior', 'is_exterior', 'outdoor', 'is_outdoor'];

    if (aliases.some((key) => this.isTruthy(raw?.[key]))) {
      return 1;
    }

    const usageText = [
      raw?.usage,
      raw?.usage_type,
      raw?.location_usage,
      raw?.application,
      raw?.placement,
      raw?.tags,
      raw?.category,
      raw?.category_name,
      raw?.description,
      raw?.name,
      raw?.product_name
    ]
      .flatMap((value) => Array.isArray(value) ? value : [value])
      .filter((value) => value !== undefined && value !== null)
      .join(' ')
      .toLowerCase();

    if (filter === 'interior') {
      return usageText.includes('interior') || usageText.includes('indoor') || usageText.includes('dalam ruang') ? 1 : 0;
    }

    return usageText.includes('exterior') || usageText.includes('outdoor') || usageText.includes('luar ruang') ? 1 : 0;
  }

  private isProductVisibleInVisualizer(product: Product): boolean {
    const raw = product as any;

    return (
      this.isTruthy(raw.is_active ?? 1) &&
      this.isTruthy(raw.category_is_active ?? 1) &&
      this.isTruthy(raw.series_is_active ?? 1)
    );
  }

  private uniqueProducts(products: Product[]): Product[] {
    const map = new Map<number, Product>();

    products.forEach((product) => {
      if (!product?.id || map.has(product.id)) {
        return;
      }

      map.set(product.id, product);
    });

    return Array.from(map.values());
  }

  private normalizeImageUrl(url?: string | null): string {
    if (!url) {
      return this.fallbackProductImageUrl;
    }

    if (
      url.startsWith('http://') ||
      url.startsWith('https://') ||
      url.startsWith('blob:') ||
      url.startsWith('/uploads') ||
      url.startsWith('uploads/')
    ) {
      return this.api.resolveAssetUrl(url.startsWith('uploads/') ? `/${url}` : url);
    }

    return url.startsWith('/') ? url.substring(1) : url;
  }

  private loadCompanyPreviewImage(): void {
    this.adminApi.getCompanyDetail(this.resolveCurrentCompanyId()).subscribe({
      next: (response) => {
        this.companyPreviewImageUrl = this.adminApi.resolveImageUrl(
          response.company?.logo_url,
          this.fallbackProductImageUrl
        );
      },
      error: () => {
        this.companyPreviewImageUrl = this.fallbackProductImageUrl;
      }
    });
  }

  safeImageUrl(imageUrl?: string | null): string {
    return imageUrl || this.fallbackProductImageUrl;
  }

  safeProductImageUrl(imageUrl?: string | null): string {
    if (!imageUrl) {
      return this.fallbackProductImageUrl;
    }

    return this.isLocalProductAssetUrl(imageUrl) ? this.fallbackProductImageUrl : imageUrl;
  }

  private toReturnTargetSurface(surface: unknown): TargetSurface | null {
    const normalized = String(surface || '').trim().toLowerCase();
    const aliases: Record<string, TargetSurface> = {
      floor: 'floor',
      lantai: 'floor',
      wall: 'wall',
      dinding: 'wall',
      ceiling: 'ceiling',
      plafon: 'ceiling',
      plafond: 'ceiling'
    };
    const value = (aliases[normalized] || normalized) as TargetSurface;
    return this.surfaceOrder.includes(value) ? value : null;
  }

  private resolveReturnAssetUrl(url: unknown): string | null {
    if (!url) {
      return null;
    }

    const text = String(url);
    if (text.startsWith('data:') || text.startsWith('blob:')) {
      return text;
    }

    return this.safeProductImageUrl(this.api.resolveBackendUrl(text) || text);
  }

  private isLocalProductAssetUrl(imageUrl: string): boolean {
    const cleanUrl = imageUrl.trim();

    if (!cleanUrl || cleanUrl.startsWith('blob:') || cleanUrl.startsWith('data:')) {
      return false;
    }

    try {
      const parsed = new URL(cleanUrl, window.location.origin);
      return parsed.pathname.replace(/^\/+/, '').toLowerCase().startsWith('assets/images/products/');
    } catch {
      return cleanUrl.replace(/^\/+/, '').toLowerCase().startsWith('assets/images/products/');
    }
  }

  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement | null;

    if (!img || img.src === this.fallbackProductImageUrl) {
      return;
    }

    img.src = this.fallbackProductImageUrl;
  }


  async selectSampleRoom(room: RoomSample): Promise<void> {
    this.errorMessage = '';
    this.successMessage = '';
    this.trackLocationUsage('room', 'template', 'select_template_room');

    await this.prepareRoomFromTemplate(room);
  }

  async onRoomUpload(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    this.trackLocationUsage('room', 'upload', 'upload_room');

    try {
      await this.prepareRoomFromFile(file);
    } finally {
      input.value = '';
    }
  }

  onTextureUpload(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    this.objectUrls.add(objectUrl);
    const customProduct: ProductTexture = {
      sku: file.name.replace(/\.[^.]+$/, '').slice(0, 24) || 'CUSTOM',
      name: 'Unggah tekstur manual',
      description: `Tekstur custom untuk ${this.selectedSurfaceLabel}.`,
      category: 'Custom',
      targetClass: this.settings.targetClass,
      thumbnailUrl: objectUrl,
      textureUrl: objectUrl,
      plankWidthM: this.settings.plankWidthM,
      plankLengthM: this.settings.plankLengthM,
      coverage: 0,
      coverageType: 'Box',
      waste: 0,
      type: 'Custom texture'
    };
    this.assignProductToActiveSurface(customProduct, file);
    this.catalogOpen = false;
    this.adjustmentOpen = true;
    input.value = '';
  }

  toggleCatalog(): void {
    if (!this.canOpenCatalog) {
      return;
    }

    this.catalogOpen = !this.catalogOpen;
    this.adjustmentOpen = false;
    this.usedProductsOpen = false;
    this.mobileAdjustMode = 'main';

    if (this.catalogOpen) {
      this.mobileCatalogStep = 'category';
      this.selectedCategory = 'all';
      this.selectedSeries = 'all';
      this.catalogSearchKeyword = '';
      this.catalogFilterOpen = false;
      this.catalogUsageFilter = 'all';
    }
  }

  toggleMobileCatalog(): void {
    const willOpen = !this.catalogOpen;

    if (willOpen) {
      this.exitCompareMode();
    }

    this.catalogOpen = willOpen;
    this.adjustmentOpen = false;
    this.usedProductsOpen = false;
    this.mobileAdjustMode = 'main';

    if (willOpen) {
      this.mobileCatalogStep = 'category';
      this.selectedCategory = 'all';
      this.selectedSeries = 'all';
      this.catalogSearchKeyword = '';
      this.catalogFilterOpen = false;
      this.catalogUsageFilter = 'all';
    }
  }

  toggleMobileAdjustment(): void {
    if (!this.activeProduct) {
      return;
    }

    const willOpen = !this.adjustmentOpen;

    if (willOpen) {
      this.exitCompareMode();
    }

    this.adjustmentOpen = willOpen;
    this.catalogOpen = false;
    this.usedProductsOpen = false;
    this.mobileAdjustMode = 'main';
  }

  toggleUsedProductsList(): void {
    if (this.usedProductCount === 0) {
      return;
    }

    if (this.isMobilePortraitViewport() || this.isMobileLandscapeViewport()) {
      const assignment = this.activeSurfaceAssignment ?? this.assignedSurfaceList[0] ?? null;

      if (assignment) {
        this.setTargetSurface(assignment.surface, assignment.instanceId || null);
      }

      this.usedProductsOpen = false;
      this.catalogOpen = false;
      this.adjustmentOpen = true;
      this.mobileAdjustMode = 'products';
      return;
    }

    this.usedProductsOpen = !this.usedProductsOpen;

    if (this.usedProductsOpen) {
      this.catalogOpen = false;
      this.adjustmentOpen = false;
      this.mobileAdjustMode = 'main';
    }
  }

  openUsedProduct(assignment: SurfaceAssignment): void {
    this.setTargetSurface(assignment.surface, assignment.instanceId || null);
    this.usedProductsOpen = false;
    this.catalogOpen = false;
    this.adjustmentOpen = true;
    this.mobileAdjustMode = 'products';
  }

  openCatalogCategory(categoryName: string, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    const normalizedCategory = this.normalizeCatalogCategory(categoryName);
    if (!normalizedCategory) {
      return;
    }

    this.selectedCategory = categoryName;
    this.selectedSeries = 'all';
    this.mobileCatalogStep = 'series';
    this.successMessage = '';
    this.errorMessage = '';
  }

  openCatalogSeries(seriesName: string, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    const normalizedSeries = this.normalizeCatalogCategory(seriesName);
    if (!normalizedSeries) {
      return;
    }

    this.selectedSeries = seriesName;
    this.mobileCatalogStep = 'product';
    this.successMessage = '';
    this.errorMessage = '';
  }


  startCatalogDrawerDrag(event: PointerEvent): void {
    this.startDrawerDrag('catalog', event);
  }

  startDrawerDrag(panel: DraggablePanel, event: PointerEvent): void {
    if (!this.isDesktopCatalogViewport()) {
      return;
    }

    if (panel === 'catalog' && !this.catalogOpen) {
      return;
    }

    if (panel === 'adjust' && (!this.adjustmentOpen || !this.activeProduct)) {
      return;
    }

    if (panel === 'usedProducts' && (!this.usedProductsOpen || this.usedProductCount === 0)) {
      return;
    }

    const target = event.target as HTMLElement | null;
    if (target?.closest('button, input, select, textarea, a')) {
      return;
    }

    event.preventDefault();
    this.activeDragPanel = panel;
    this.catalogDrawerDragging = panel === 'catalog';
    this.adjustDrawerDragging = panel === 'adjust';
    this.usedProductsDragging = panel === 'usedProducts';
    this.drawerDragStartX = event.clientX;
    this.drawerDragStartY = event.clientY;
    const offset = this.getPanelOffset(panel);
    this.drawerStartOffsetX = offset.x;
    this.drawerStartOffsetY = offset.y;
  }

  @HostListener('document:pointermove', ['$event'])
  onDocumentCatalogDrawerPointerMove(event: PointerEvent): void {
    if (!this.activeDragPanel) {
      return;
    }

    const nextX = this.drawerStartOffsetX + event.clientX - this.drawerDragStartX;
    const nextY = this.drawerStartOffsetY + event.clientY - this.drawerDragStartY;
    const bounds = this.drawerDragBounds(this.activeDragPanel);

    this.setPanelOffset(this.activeDragPanel, {
      x: this.clamp(nextX, bounds.minX, bounds.maxX),
      y: this.clamp(nextY, bounds.minY, bounds.maxY)
    });
  }

  @HostListener('document:pointerup')
  onDocumentCatalogDrawerPointerUp(): void {
    this.activeDragPanel = null;
    this.catalogDrawerDragging = false;
    this.adjustDrawerDragging = false;
    this.usedProductsDragging = false;
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateSampleColumnCount();

    this.clampPanelOffsets();
  }

  private updateSampleColumnCount(): void {
    const previousColumnCount = this.sampleColumnCount;

    if (typeof window === 'undefined') {
      this.sampleColumnCount = 5;
      if (this.sampleColumnCount !== previousColumnCount) {
        this.rebuildSampleRoomColumns();
      }
      return;
    }

    if (this.isMobileLandscapeViewport()) {
      this.sampleColumnCount = 4;
      if (this.sampleColumnCount !== previousColumnCount) {
        this.rebuildSampleRoomColumns();
      }
      return;
    }

    if (window.innerWidth <= 640) {
      this.sampleColumnCount = 2;
      if (this.sampleColumnCount !== previousColumnCount) {
        this.rebuildSampleRoomColumns();
      }
      return;
    }

    if (window.innerWidth <= 1023) {
      this.sampleColumnCount = 3;
      if (this.sampleColumnCount !== previousColumnCount) {
        this.rebuildSampleRoomColumns();
      }
      return;
    }

    if (window.innerWidth <= 1320) {
      this.sampleColumnCount = 4;
      if (this.sampleColumnCount !== previousColumnCount) {
        this.rebuildSampleRoomColumns();
      }
      return;
    }

    this.sampleColumnCount = 5;
    if (this.sampleColumnCount !== previousColumnCount) {
      this.rebuildSampleRoomColumns();
    }
  }

  private isDesktopCatalogViewport(): boolean {
    return typeof window !== 'undefined' && window.innerWidth > 768 && !this.isMobileLandscapeViewport();
  }

  isMobileLandscapeViewport(): boolean {
    return typeof window !== 'undefined'
      && window.matchMedia('(orientation: landscape) and (max-width: 950px) and (max-height: 520px)').matches;
  }

  private isMobilePortraitViewport(): boolean {
    return typeof window !== 'undefined'
      && window.matchMedia('(orientation: portrait) and (max-width: 1023px)').matches;
  }

  async toggleVisualizerOrientation(): Promise<void> {
    if (typeof window === 'undefined') {
      return;
    }

    const shouldUseLandscape = !this.isMobileLandscapeViewport();
    const orientation = window.screen?.orientation as (ScreenOrientation & {
      lock?: (orientation: VisualizerOrientationLock) => Promise<void>;
      unlock?: () => void;
    }) | undefined;

    try {
      if (shouldUseLandscape) {
        await orientation?.lock?.('landscape');
        return;
      }

      orientation?.unlock?.();
    } catch (error) {
      console.warn('Orientasi layar tidak bisa dikunci oleh browser:', error);
      this.showError('Browser tidak mengizinkan ubah orientasi otomatis. Putar perangkat secara manual.');
    }
  }

  private drawerDragBounds(panel: DraggablePanel): { minX: number; maxX: number; minY: number; maxY: number } {
    if (typeof window === 'undefined') {
      return { minX: -480, maxX: 160, minY: -120, maxY: 120 };
    }

    if (panel === 'usedProducts') {
      return {
        minX: -16,
        maxX: Math.max(80, window.innerWidth - 390),
        minY: -24,
        maxY: Math.max(80, window.innerHeight - 260),
      };
    }

    return {
      minX: -Math.round(window.innerWidth * 0.58),
      maxX: Math.round(window.innerWidth * 0.16),
      minY: -Math.round(window.innerHeight * 0.18),
      maxY: Math.round(window.innerHeight * 0.28),
    };
  }

  private getPanelOffset(panel: DraggablePanel): { x: number; y: number } {
    if (panel === 'adjust') {
      return { x: this.adjustDrawerOffsetX, y: this.adjustDrawerOffsetY };
    }

    if (panel === 'usedProducts') {
      return { x: this.usedProductsOffsetX, y: this.usedProductsOffsetY };
    }

    return { x: this.catalogDrawerOffsetX, y: this.catalogDrawerOffsetY };
  }

  private setPanelOffset(panel: DraggablePanel, offset: { x: number; y: number }): void {
    if (panel === 'adjust') {
      this.adjustDrawerOffsetX = offset.x;
      this.adjustDrawerOffsetY = offset.y;
      return;
    }

    if (panel === 'usedProducts') {
      this.usedProductsOffsetX = offset.x;
      this.usedProductsOffsetY = offset.y;
      return;
    }

    this.catalogDrawerOffsetX = offset.x;
    this.catalogDrawerOffsetY = offset.y;
  }

  private clampPanelOffsets(): void {
    (['catalog', 'adjust', 'usedProducts'] as DraggablePanel[]).forEach((panel) => {
      const offset = this.getPanelOffset(panel);
      const bounds = this.drawerDragBounds(panel);
      this.setPanelOffset(panel, {
        x: this.clamp(offset.x, bounds.minX, bounds.maxX),
        y: this.clamp(offset.y, bounds.minY, bounds.maxY),
      });
    });
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
  }

  @HostListener('document:pointerdown', ['$event'])
  onDocumentDesktopCategoryPointerDown(event: PointerEvent): void {
    const elements = document.elementsFromPoint(event.clientX, event.clientY);

    if (
      this.patternDropdownOpen &&
      !elements.some((element) => element instanceof HTMLElement && !!element.closest('.pattern-dropdown'))
    ) {
      this.patternDropdownOpen = false;
    }

    if (!this.catalogOpen || (this.mobileCatalogStep !== 'category' && this.mobileCatalogStep !== 'series')) {
      return;
    }

    if (typeof window !== 'undefined' && window.innerWidth <= 768) {
      return;
    }

    if (this.mobileCatalogStep === 'category') {
      const categoryElement = elements.find((element) =>
        element instanceof HTMLElement && element.dataset['desktopCatalogCategory']
      ) as HTMLElement | undefined;

      const categoryName = categoryElement?.dataset['desktopCatalogCategory'];
      if (categoryName) {
        this.openCatalogCategory(categoryName, event);
      }

      return;
    }

    const seriesElement = elements.find((element) =>
      element instanceof HTMLElement && element.dataset['desktopCatalogSeries']
    ) as HTMLElement | undefined;

    const seriesName = seriesElement?.dataset['desktopCatalogSeries'];
    if (seriesName) {
      this.openCatalogSeries(seriesName, event);
    }
  }

  openMobileCatalogCategory(categoryName: string): void {
    this.openCatalogCategory(categoryName);
  }

  backToCatalogCategories(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    this.mobileCatalogStep = 'category';
    this.selectedCategory = 'all';
    this.selectedSeries = 'all';
  }

  backToCatalogSeries(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    this.mobileCatalogStep = 'series';
    this.selectedSeries = 'all';
  }

  backToMobileCatalogCategories(): void {
    this.backToCatalogCategories();
  }

  openCatalogFromAdjustment(): void {
    this.catalogOpen = true;
    this.adjustmentOpen = false;
    this.usedProductsOpen = false;
    this.mobileAdjustMode = 'main';
    this.mobileCatalogStep = 'category';
    this.selectedCategory = 'all';
    this.selectedSeries = 'all';
    this.catalogSearchKeyword = '';
    this.catalogFilterOpen = false;
    this.catalogUsageFilter = 'all';
  }

  selectProduct(product: ProductTexture): void {
    this.assignProductToActiveSurface(product, null);
    this.catalogOpen = false;
    this.adjustmentOpen = true;
    this.usedProductsOpen = false;
    this.mobileCatalogStep = 'product';
  }

  setTargetSurface(surface: TargetSurface, instanceId: string | null = null): void {
    if (surface === this.settings.targetClass && (instanceId || null) === (this.activeInstanceId || null)) {
      return;
    }

    this.syncActiveAssignmentSettings();
    this.patternDropdownOpen = false;
    this.activeInstanceId = instanceId || null;
    const key = this.assignmentKey(surface, this.activeInstanceId);
    const assignment =
      this.surfaceAssignments[key] ??
      (this.isMultiInstanceSurface(surface) && this.activeInstanceId
        ? this.surfaceAssignments[this.assignmentKey(surface, null)]
        : null) ??
      null;
    this.settings = assignment?.settings ?? this.settingsForSurface(surface);
    const prepared = this.preparedSurfaces[surface] ?? null;
    const preparedInstance = this.activeInstanceId
      ? prepared?.instances.find((item) => item.id === this.activeInstanceId) ?? null
      : null;
    const hotspot = this.detectedSurfaceHotspots.find(
      (item) => item.surface === surface && (item.instanceId || null) === (this.activeInstanceId || null)
    );
    const hotspotQuad = hotspot?.polygon?.length ? this.polygonToPerspectivePoints(hotspot.polygon) : null;
    const preparedQuad = preparedInstance?.quad || prepared?.quad || prepared?.instances?.[0]?.quad || null;

    this.selectedProduct = assignment?.product ?? null;
    this.selectedProductTextureFile = assignment?.customTextureFile ?? null;
    this.urls.texture = assignment?.textureUrl ?? null;
    this.urls.mask = assignment?.maskUrl ?? preparedInstance?.maskUrl ?? prepared?.maskUrl ?? this.urls.mask;
    this.urls.slice = assignment?.sliceUrl ?? preparedInstance?.sliceUrl ?? prepared?.sliceUrl ?? this.urls.slice;
    this.selectedCategory = 'all';
    this.selectedSeries = 'all';
    this.catalogSearchKeyword = '';
    this.catalogFilterOpen = false;
    this.catalogUsageFilter = 'all';
    this.mobileCatalogStep = 'category';

    const nextQuad = this.shouldUsePerspectiveProjection(surface)
      ? (assignment?.floorQuad || preparedQuad || hotspotQuad || null)
      : null;

    this.settings = {
      ...this.settings,
      perspectivePoints: nextQuad
    };
  }

  selectSurfaceFromHotspot(hotspot: SurfaceHotspot): void {
    this.setTargetSurface(hotspot.surface, hotspot.instanceId || null);
    this.catalogOpen = true;
    this.adjustmentOpen = false;
    this.usedProductsOpen = false;
    this.mobileCatalogStep = 'category';
    this.selectedCategory = 'all';
    this.selectedSeries = 'all';
    this.catalogSearchKeyword = '';
    this.catalogFilterOpen = false;
    this.catalogUsageFilter = 'all';
  }

  setViewerMode(mode: ViewerMode): void {
    if ((mode === 'preview' || mode === 'compare') && !this.activeSurfaceAssignment) {
      return;
    }

    this.viewerMode = mode;
  }

  private exitCompareMode(): void {
    if (this.viewerMode !== 'compare') {
      return;
    }

    this.viewerMode = this.activeSurfaceAssignment ? 'preview' : 'original';
  }

  activateMobileCompare(): void {
    if (!this.activeSurfaceAssignment) {
      return;
    }

    this.comparePosition = 0.5;
    this.setViewerMode('compare');
    this.mobileCompareControlsOpen = true;
    this.adjustmentOpen = false;
    this.mobileAdjustMode = 'main';
  }

  toggleMobileCompareControls(): void {
    if (!this.activeSurfaceAssignment) {
      return;
    }

    if (this.mobileCompareControlsOpen || this.viewerMode === 'compare') {
      this.mobileCompareControlsOpen = false;
      this.exitCompareMode();
      return;
    }

    this.activateMobileCompare();
  }

  removeSurfaceAssignment(surface: TargetSurface = this.settings.targetClass): void {
    const activeKey = this.activeAssignmentKey();
    const key = surface === this.settings.targetClass
      ? this.isMultiInstanceSurface(surface) && this.activeInstanceId && !this.surfaceAssignments[activeKey]
        ? this.assignmentKey(surface, null)
        : activeKey
      : this.assignmentKey(surface, null);
    const nextAssignments = { ...this.surfaceAssignments };
    delete nextAssignments[key];
    this.surfaceAssignments = nextAssignments;
    if (surface === this.settings.targetClass) {
      this.selectedProduct = null;
      this.selectedProductTextureFile = null;
      this.urls.texture = null;
    }
    this.rebuildPreviewLayers();
    this.showSuccess(`Produk untuk ${this.surfaceLabel(surface)} dihapus.`);
  }

  resetSurfaceAssignments(): void {
    this.surfaceAssignments = {};
    this.selectedProduct = null;
    this.selectedProductTextureFile = null;
    this.urls.texture = null;
    this.mobileCatalogStep = 'category';
    this.selectedCategory = 'all';
    this.selectedSeries = 'all';
    this.catalogSearchKeyword = '';
    this.rebuildPreviewLayers();
    this.showSuccess('Semua pilihan produk surface sudah dikosongkan.');
  }

  async generate(): Promise<void> {
    this.syncActiveAssignmentSettings();
    const assignments = this.getOrderedAssignments();

    if (assignments.length === 0) {
      this.showError('Klik titik pada gambar lalu pilih minimal satu produk.');
      return;
    }

    if (this.activeRoomSource === 'template') {
      const floorAssignment = assignments.find((assignment) => assignment.surface === 'floor');

      if (!floorAssignment) {
        this.rebuildPreviewLayers();
        this.viewerMode = 'preview';
        return;
      }

      const templateId = Number(this.selectedTemplateId || 0);
      const productId = Number(
        floorAssignment.product.id ||
        String(floorAssignment.product.code || floorAssignment.product.sku || '').match(/PRD-(\d+)/)?.[1] ||
        0
      );

      if (!templateId || !productId) {
        this.showError('Template/product id tidak lengkap untuk render lantai.');
        return;
      }

      this.api.setBaseUrl(this.apiBaseUrl);
      this.isGenerating = true;
      this.errorMessage = '';
      this.successMessage = '';

      try {
        const response = await firstValueFrom(this.api.renderTemplateFloor({
          template_id: templateId,
          surface_instance_id: floorAssignment.instanceId || 'floor_0',
          product_id: productId,
          pattern: this.isKisiOnlySurface(floorAssignment.surface) ? 'straight' : String(floorAssignment.settings.pattern || 'grid'),
          layout_direction: 'depth',
          rotation: Number(floorAssignment.settings.rotationAngle || 0),
          offset_x: Number(floorAssignment.settings.textureOffsetX || 0),
          offset_y: Number(floorAssignment.settings.textureOffsetY || 0),
          repeat_scale: Number(floorAssignment.settings.textureScale || floorAssignment.product.textureRepeatScale || 1),
          lighting_blend: false
        })) as GenerateResponse;

        if (response.status !== 'success' || !response.result_url) {
          throw new Error(response.error || 'Backend render lantai tidak mengembalikan result_url.');
        }

        this.lastResponse = response;
        this.urls = {
          original: this.selectedRoomUrl,
          texture: floorAssignment.textureUrl,
          result: this.api.resolveBackendUrl(response.result_url),
          mask: this.api.resolveBackendUrl(response.mask_url || (response.render_meta as any)?.mask_url),
          slice: null
        };
        this.comparePosition = 0.5;
        this.viewerMode = 'result';
        this.trackLocationUsage(floorAssignment.surface || 'room', 'generate', 'generate_visualizer');
        this.showSuccess('Render lantai template berhasil.');
      } catch (error) {
        this.showError(this.formatError(error));
      } finally {
        this.isGenerating = false;
      }
      return;
    }

    if (!this.selectedRoomFile) {
      this.showError('Pilih foto ruangan terlebih dahulu.');
      return;
    }

    this.api.setBaseUrl(this.apiBaseUrl);
    this.isGenerating = true;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      let workingImageFile: File = this.selectedRoomFile;
      let finalResponse: GenerateResponse | null = null;
      let lastAssignment: SurfaceAssignment | null = null;
      const completedSurfaces: TargetSurface[] = [];

      for (let index = 0; index < assignments.length; index += 1) {
        const assignment = assignments[index];
        const textureFile = await this.textureFileForAssignment(assignment);
        const requestSettings: GenerateSettings = {
          ...assignment.settings,
          targetClass: assignment.surface,
          plankWidthM:
            Number(assignment.product.plankWidthM || 0) ||
            Number(assignment.product.width ? assignment.product.width / 1000 : 0) ||
            assignment.settings.plankWidthM,
          plankLengthM:
            Number(assignment.product.plankLengthM || 0) ||
            Number(assignment.product.height ? assignment.product.height / 1000 : 0) ||
            assignment.settings.plankLengthM
        };

        const response = await firstValueFrom(this.api.generate(workingImageFile, textureFile, requestSettings)) as GenerateResponse;
        if (response.status !== 'success') {
          throw new Error(`${this.surfaceLabel(assignment.surface)}: ${response.error || 'Backend mengembalikan status error.'}`);
        }
        if (!response.result_url) {
          throw new Error(`${this.surfaceLabel(assignment.surface)}: Backend tidak mengembalikan result_url.`);
        }

        finalResponse = response;
        lastAssignment = assignment;
        completedSurfaces.push(assignment.surface);
        this.applyGenerateResponseToAssignment(assignment, response);

        if (index < assignments.length - 1) {
          workingImageFile = await firstValueFrom(
            this.api.fetchAssetAsFile(response.result_url, `frantinco-layer-${assignment.surface}.jpg`)
          );
        }
      }

      if (!finalResponse || !lastAssignment) {
        throw new Error('Tidak ada surface yang diproses.');
      }

      this.lastResponse = finalResponse;
      this.urls = {
        original: this.selectedRoomUrl,
        texture: lastAssignment.textureUrl,
        result: this.api.resolveBackendUrl(finalResponse.result_url),
        mask: this.api.resolveBackendUrl(finalResponse.mask_url),
        slice: this.api.resolveBackendUrl(finalResponse.slice_url)
      };
      this.comparePosition = 0.5;
      this.viewerMode = 'result';
      this.trackLocationUsage(lastAssignment.surface || 'room', 'generate', 'generate_visualizer');
      this.showSuccess(`Visualisasi berhasil untuk ${completedSurfaces.map((surface) => this.surfaceLabel(surface)).join(', ')}.`);
    } catch (error) {
      this.showError(this.formatError(error));
    } finally {
      this.isGenerating = false;
    }
  }

  clearResult(): void {
    this.lastResponse = null;
    this.urls = {
      original: this.selectedRoomUrl,
      texture: this.selectedProductTextureUrl,
      result: null,
      mask: null,
      slice: null
    };
    this.viewerMode = 'preview';
    this.successMessage = '';
  }

  async downloadResult(): Promise<void> {
    const imageUrl =
      this.roomCanvas?.exportCurrentImageOnlyDataUrl() ||
      this.roomCanvas?.exportCurrentImageDataUrl() ||
      this.urls.result ||
      this.urls.original;

    if (!imageUrl) {
      return;
    }

    const anchor = document.createElement('a');
    anchor.href = imageUrl;
    anchor.download = this.lastResponse?.result_filename || `frantinco-visualizer-${Date.now()}.jpg`;
    anchor.target = '_blank';
    anchor.rel = 'noopener';
    anchor.click();
  }

  sendToEstimator(): void {
    const items = this.assignedSurfaceList.map((assignment) => {
      const fallbackArea = assignment.aiLength && assignment.aiWidth ? assignment.aiLength * assignment.aiWidth : undefined;
      const prepared = this.preparedSurfaces[assignment.surface] ?? null;
      const preparedInstance = assignment.instanceId
        ? prepared?.instances.find((item) => item.id === assignment.instanceId) ?? null
        : null;
      const hotspot = this.detectedSurfaceHotspots.find(
        (item) => item.surface === assignment.surface && (item.instanceId || null) === (assignment.instanceId || null)
      );
      return {
        surface: assignment.surface,
        instanceId: assignment.instanceId || null,
        surfaceLabel: this.usedAssignmentLabel(assignment),
        product: {
          sku: assignment.product.sku,
          code: assignment.product.code || assignment.product.sku,
          name: assignment.product.name,
          description: assignment.product.description,
          category: assignment.product.category,
          targetClass: assignment.product.targetClass,
          thumbnailUrl: assignment.product.thumbnailUrl,
          textureUrl: assignment.product.textureUrl,
          coverage: assignment.product.coverage,
          coverageType: assignment.product.coverageType,
          waste: assignment.product.waste,
          width: assignment.product.width,
          height: assignment.product.height,
          sizeType: assignment.product.sizeType
        },
        aiLength: assignment.aiLength || null,
        aiWidth: assignment.aiWidth || null,
        areaM2: assignment.areaM2 || fallbackArea || null,
        maskUrl: assignment.maskUrl || null,
        sliceUrl: assignment.sliceUrl || null,
        floorQuad: assignment.floorQuad || assignment.settings.perspectivePoints || preparedInstance?.quad || prepared?.quad || null,
        settings: assignment.settings,
        polygon: hotspot?.polygon || preparedInstance?.polygon || null,
        bbox: preparedInstance?.bbox || null,
        xRatio: hotspot?.xRatio ?? null,
        yRatio: hotspot?.yRatio ?? null
      };
    });

    if (items.length === 0) {
      this.showError('Belum ada produk yang dipilih untuk dikirim ke estimator.');
      return;
    }

    const visualizerSnapshotUrl =
      this.roomCanvas?.exportCurrentImageOnlyDataUrl() ||
      this.urls.result ||
      this.roomCanvas?.exportCurrentImageDataUrl() ||
      this.urls.original;

    const payload = {
      generatedImageUrl: visualizerSnapshotUrl,
      originalImageUrl: this.urls.original || this.selectedRoomUrl || null,
      imageWidth: this.roomCanvas?.imageWidth || null,
      imageHeight: this.roomCanvas?.imageHeight || null,
      hotspots: this.surfaceHotspots,
      items
    };

    localStorage.setItem('frantinco_estimator_payload', JSON.stringify(payload));
    this.router.navigate(['/estimator']);
  }

  private restoreFromEstimatorReturnPayload(): void {
    const rawPayload = this.pendingEstimatorReturnPayload || localStorage.getItem('frantinco_visualizer_return_payload');
    if (!rawPayload) {
      return;
    }

    if (!this.productsLoaded) {
      this.pendingEstimatorReturnPayload = rawPayload;
      return;
    }

    this.pendingEstimatorReturnPayload = null;
    localStorage.removeItem('frantinco_visualizer_return_payload');

    try {
      const payload = JSON.parse(rawPayload);
      const items = Array.isArray(payload?.items) ? payload.items : [];
      const returnedHotspots = Array.isArray(payload?.hotspots) ? payload.hotspots : [];
      const assignments: Record<string, SurfaceAssignment | undefined> = {};
      const preparedBySurface: Partial<Record<TargetSurface, PreparedSurfaceView>> = {};
      const hotspots: SurfaceHotspot[] = [];
      const originalUrl = payload?.originalImageUrl || payload?.generatedImageUrl || null;
      const resultUrl = payload?.generatedImageUrl || null;
      const payloadImageWidth = Number(payload?.imageWidth || payload?.image?.width || 0);
      const payloadImageHeight = Number(payload?.imageHeight || payload?.image?.height || 0);

      for (const item of items) {
        const surface = this.toReturnTargetSurface(item?.surface);
        if (!surface) {
          continue;
        }

        const instanceId = item?.instanceId || null;
        const label = item?.surfaceLabel || this.surfaceLabel(surface);
        const maskUrl = this.resolveReturnAssetUrl(item?.maskUrl);
        const sliceUrl = this.resolveReturnAssetUrl(item?.sliceUrl);
        const textureUrl = this.resolveReturnAssetUrl(item?.product?.textureUrl) || this.fallbackProductImageUrl;
        const thumbnailUrl = this.resolveReturnAssetUrl(item?.product?.thumbnailUrl) || this.fallbackProductImageUrl;
        const returnedSettings = item?.settings || {};
        const perspectivePoints = item?.floorQuad || returnedSettings?.perspectivePoints || null;
        const settings: GenerateSettings = {
          ...this.settingsForSurface(surface),
          ...returnedSettings,
          targetClass: surface,
          perspectivePoints
        };
        const product: ProductTexture = {
          ...item.product,
          sku: item?.product?.sku || item?.product?.code || 'PRODUCT',
          code: item?.product?.code || item?.product?.sku || 'PRODUCT',
          name: item?.product?.name || 'Produk',
          description: item?.product?.description || '',
          category: item?.product?.category || '',
          targetClass: surface,
          thumbnailUrl,
          textureUrl
        };
        const key = this.assignmentKey(surface, instanceId);

        assignments[key] = {
          surface,
          instanceId,
          product,
          settings,
          textureUrl,
          customTextureFile: null,
          aiLength: Number(item?.aiLength || 0) || undefined,
          aiWidth: Number(item?.aiWidth || 0) || undefined,
          areaM2: Number(item?.areaM2 || 0) || undefined,
          floorQuad: perspectivePoints,
          maskUrl,
          sliceUrl
        };

        const preparedInstance: PreparedSurfaceInstanceView = {
          id: instanceId || `${surface}_return`,
          surface,
          label,
          maskUrl,
          sliceUrl,
          quad: perspectivePoints,
          bbox: item?.bbox || null,
          polygon: item?.polygon || undefined
        };
        const existingPrepared = preparedBySurface[surface];
        preparedBySurface[surface] = {
          surface,
          available: true,
          maskUrl: existingPrepared?.maskUrl || maskUrl,
          sliceUrl: existingPrepared?.sliceUrl || sliceUrl,
          quad: existingPrepared?.quad || perspectivePoints,
          instances: [
            ...(existingPrepared?.instances || []),
            preparedInstance
          ]
        };

        const position = this.resolveReturnedHotspotPosition(item, surface, payloadImageWidth, payloadImageHeight);
        hotspots.push({
          surface,
          instanceId,
          label,
          hint: `${label} - dari estimator`,
          xRatio: position.xRatio,
          yRatio: position.yRatio,
          productSku: product.sku,
          productName: product.name,
          productThumbnailUrl: product.thumbnailUrl,
          isConfigured: true,
          polygon: item?.polygon || undefined
        });
      }

      const restoredAssignments = Object.values(assignments).filter(Boolean).length;
      if (restoredAssignments === 0) {
        return;
      }

      const hotspotByKey = new Map<string, SurfaceHotspot>();
      for (const returnedHotspot of returnedHotspots) {
        const surface = this.toReturnTargetSurface(returnedHotspot?.surface);
        if (!surface) {
          continue;
        }

        const instanceId = returnedHotspot?.instanceId || null;
        const key = this.assignmentKey(surface, instanceId);
        const assignment = assignments[key];
        const position = this.resolveReturnedHotspotPosition(returnedHotspot, surface, payloadImageWidth, payloadImageHeight);
        const label = returnedHotspot?.label || assignment?.settings?.targetClass || this.surfaceLabel(surface);

        hotspotByKey.set(key, {
          surface,
          instanceId,
          label,
          hint: returnedHotspot?.hint || `${label} - area ruangan`,
          xRatio: position.xRatio,
          yRatio: position.yRatio,
          productSku: assignment?.product.sku ?? null,
          productName: assignment?.product.name ?? null,
          productThumbnailUrl: assignment?.product.thumbnailUrl ?? null,
          isConfigured: !!assignment,
          polygon: returnedHotspot?.polygon || undefined
        });
      }

      for (const hotspot of hotspots) {
        const key = this.hotspotKey(hotspot);
        const existingHotspot = hotspotByKey.get(key);
        hotspotByKey.set(key, existingHotspot
          ? {
              ...hotspot,
              ...existingHotspot,
              productSku: hotspot.productSku ?? existingHotspot.productSku,
              productName: hotspot.productName ?? existingHotspot.productName,
              productThumbnailUrl: hotspot.productThumbnailUrl ?? existingHotspot.productThumbnailUrl,
              isConfigured: hotspot.isConfigured || existingHotspot.isConfigured
            }
          : hotspot);
      }

      this.selectedRoomUrl = originalUrl;
      this.activeRoomSource = originalUrl ? 'template' : null;
      this.surfaceAssignments = assignments;
      this.preparedSurfaces = preparedBySurface;
      this.detectedSurfaceHotspots = hotspotByKey.size > 0 ? Array.from(hotspotByKey.values()) : hotspots;
      this.urls = {
        original: originalUrl,
        texture: null,
        result: resultUrl,
        mask: hotspots[0] ? assignments[this.assignmentKey(hotspots[0].surface, hotspots[0].instanceId)]?.maskUrl || null : null,
        slice: hotspots[0] ? assignments[this.assignmentKey(hotspots[0].surface, hotspots[0].instanceId)]?.sliceUrl || null : null
      };

      if (hotspots[0]) {
        this.activeInstanceId = hotspots[0].instanceId || null;
        this.settings = assignments[this.assignmentKey(hotspots[0].surface, hotspots[0].instanceId)]?.settings || this.settingsForSurface(hotspots[0].surface);
        this.selectedProduct = assignments[this.assignmentKey(hotspots[0].surface, hotspots[0].instanceId)]?.product || null;
      }

      this.rebuildPreviewLayers();
      this.viewerMode = this.previewLayers.length > 0 ? 'preview' : 'original';
      this.catalogOpen = false;
      this.adjustmentOpen = true;
      this.mobileAdjustMode = 'products';
      this.mobileCatalogStep = 'category';
      this.selectedCategory = 'all';
      this.selectedSeries = 'all';
      this.catalogSearchKeyword = '';
      this.catalogFilterOpen = false;
      this.catalogUsageFilter = 'all';
    } catch (error) {
      console.error('Payload balik visualizer tidak valid:', error);
    }
  }

  private resolveReturnedHotspotPosition(
    source: any,
    surface: TargetSurface,
    imageWidth: number,
    imageHeight: number
  ): { xRatio: number; yRatio: number } {
    const xRatio = Number(source?.xRatio);
    const yRatio = Number(source?.yRatio);

    if (Number.isFinite(xRatio) && Number.isFinite(yRatio)) {
      return {
        xRatio: this.clampNumber(xRatio, 0, 1),
        yRatio: this.clampNumber(yRatio, 0, 1)
      };
    }

    const polygon = this.normalizePolygon(source?.polygon);
    if (polygon.length > 0 && imageWidth > 0 && imageHeight > 0) {
      return this.getPolygonCenterRatio(polygon, imageWidth, imageHeight);
    }

    const bbox = source?.bbox || null;
    const x1 = Number(bbox?.x1 ?? bbox?.x ?? NaN);
    const y1 = Number(bbox?.y1 ?? bbox?.y ?? NaN);
    const x2 = Number(bbox?.x2 ?? (Number.isFinite(x1) ? x1 + Number(bbox?.width || 0) : NaN));
    const y2 = Number(bbox?.y2 ?? (Number.isFinite(y1) ? y1 + Number(bbox?.height || 0) : NaN));

    if ([x1, y1, x2, y2].every((value) => Number.isFinite(value))) {
      const centerX = (x1 + x2) / 2;
      const centerY = (y1 + y2) / 2;
      const bboxLooksNormalized = Math.max(Math.abs(x1), Math.abs(y1), Math.abs(x2), Math.abs(y2)) <= 1;

      return {
        xRatio: this.clampNumber(bboxLooksNormalized || imageWidth <= 0 ? centerX : centerX / imageWidth, 0, 1),
        yRatio: this.clampNumber(bboxLooksNormalized || imageHeight <= 0 ? centerY : centerY / imageHeight, 0, 1)
      };
    }

    return this.hotspotPositions[surface] || { xRatio: 0.5, yRatio: 0.5 };
  }

  openQuoteModal(): void {
    if (this.assignedSurfaceList.length === 0) {
      this.showError('Belum ada produk yang dipilih.');
      return;
    }

    this.exitCompareMode();
    this.quoteProducts = this.buildQuoteProducts();
    this.quoteError = '';
    this.quoteSubmitting = false;
    this.quoteModalState = 'whatsapp_form';
  }

  closeQuoteModal(): void {
    this.quoteModalState = 'closed';
    this.quoteError = '';
    this.quoteSubmitting = false;
  }

  async submitQuoteWhatsapp(): Promise<void> {
    if (!this.validateQuoteForm()) {
      return;
    }

    this.quoteSubmitting = true;
    this.quoteError = '';

    try {
      const response = await firstValueFrom(
        this.api.createQuoteWhatsappLink(this.buildQuotePayload())
      );

      if (!response?.success || !response.whatsappUrl) {
        throw new Error(response?.message || 'Link WhatsApp gagal dibuat.');
      }

      window.open(response.whatsappUrl, '_blank');
      this.closeQuoteModal();
      this.showSuccess(response.message || 'Pesan WhatsApp berhasil dibuat.');
    } catch (error) {
      this.quoteError = this.formatError(error);
    } finally {
      this.quoteSubmitting = false;
    }
  }

  surfaceLabel(surface: TargetSurface): string {
    return this.surfaceOptions.find((option) => option.value === surface)?.label ?? surface;
  }

  assignmentFor(surface: TargetSurface): SurfaceAssignment | null {
    const direct = this.surfaceAssignments[this.assignmentKey(surface, null)] ?? null;
    if (direct) {
      return direct;
    }

    return this.getOrderedAssignments().find((assignment) => assignment.surface === surface) ?? null;
  }

  assignmentSummaryFor(surface: TargetSurface): string {
    const progress = this.surfaceSelectionProgress(surface);
    if (progress && progress.total > 1) {
      return progress.selected > 0 ? `${progress.selected}/${progress.total} dipilih` : 'Belum dipilih';
    }

    const assignment = this.assignmentFor(surface);
    return assignment ? `${assignment.product.sku} dipilih` : 'Belum dipilih';
  }

  private surfaceSelectionProgress(surface: TargetSurface): { selected: number; total: number } | null {
    if (!this.isMultiInstanceSurface(surface)) {
      return null;
    }

    const instanceIds = this.surfaceInstanceIds(surface);
    if (instanceIds.length <= 1) {
      return null;
    }

    const directAssignment = this.surfaceAssignments[this.assignmentKey(surface, null)] ?? null;
    if (directAssignment) {
      return { selected: instanceIds.length, total: instanceIds.length };
    }

    const selected = instanceIds.filter((instanceId) =>
      !!this.surfaceAssignments[this.assignmentKey(surface, instanceId)]
    ).length;

    return { selected, total: instanceIds.length };
  }

  private surfaceInstanceIds(surface: TargetSurface): string[] {
    const ids = new Set<string>();

    for (const hotspot of this.detectedSurfaceHotspots) {
      if (hotspot.surface === surface && hotspot.instanceId) {
        ids.add(hotspot.instanceId);
      }
    }

    const prepared = this.preparedSurfaces[surface] ?? null;
    for (const instance of prepared?.instances || []) {
      if (instance.id) {
        ids.add(instance.id);
      }
    }

    return Array.from(ids);
  }

  isAssignedProduct(product: ProductTexture): boolean {
    return this.activeSurfaceAssignment?.product.sku === product.sku;
  }

  trackByQuoteProduct(index: number, item: QuoteProductFormItem): string {
    return `${item.surface}:${item.productCode}:${index}`;
  }

  private buildQuoteProducts(): QuoteProductFormItem[] {
    return this.assignedSurfaceList.map((assignment) => {
      const area = this.quoteAreaForAssignment(assignment);
      const coverage = Number(assignment.product.coverage || 0);
      const estimatedBoxQty = coverage > 0 && area
        ? Math.ceil(area / coverage)
        : 1;

      return {
        surface: this.usedAssignmentLabel(assignment),
        productCode: assignment.product.code || assignment.product.sku,
        productName: assignment.product.name || '',
        pattern: this.patternLabel(assignment.settings.pattern),
        boxQty: Math.max(1, estimatedBoxQty),
        area
      };
    });
  }

  private buildQuotePayload(): QuoteRequestPayload {
    const payload: QuoteRequestPayload = {
      roomName: this.activeRoomTitle,
      roomType: this.activeRoomSource === 'template' ? 'Foto Template' : 'Upload Manual',
      areaCount: this.detectedSurfaceHotspots.length || this.availableSurfaceOptions.length || this.configuredSurfaceCount,
      selectedProducts: this.quoteProducts.map((item) => ({
        surface: item.surface,
        productCode: item.productCode,
        productName: item.productName,
        pattern: item.pattern,
        boxQty: Number(item.boxQty),
        area: item.area
      })),
      note: this.quoteNote.trim()
    };

    return payload;
  }

  private validateQuoteForm(): boolean {
    if (this.quoteProducts.length === 0) {
      this.quoteError = 'Belum ada produk yang dipilih.';
      return false;
    }

    const invalidBox = this.quoteProducts.some((item) => !Number.isFinite(Number(item.boxQty)) || Number(item.boxQty) < 1);
    if (invalidBox) {
      this.quoteError = 'Estimasi box wajib angka minimal 1.';
      return false;
    }

    this.quoteError = '';
    return true;
  }

  private quoteAreaForAssignment(assignment: SurfaceAssignment): number | null {
    const fallbackArea = assignment.aiLength && assignment.aiWidth
      ? assignment.aiLength * assignment.aiWidth
      : null;
    const area = Number(assignment.areaM2 || fallbackArea || 0);

    return Number.isFinite(area) && area > 0 ? Number(area.toFixed(2)) : null;
  }

  private patternLabel(pattern: unknown): string {
    return this.patternDisplayLabel(pattern);
  }

  private setRoomFile(file: File, previewUrl: string): void {
    this.selectedRoomFile = file;
    this.selectedRoomUrl = previewUrl;
    this.urls = {
      ...this.urls,
      original: previewUrl,
      result: null,
      mask: null,
      slice: null
    };
    this.lastResponse = null;
    this.viewerMode = 'original';
    this.activeInstanceId = null;
    this.detectedSurfaceHotspots = [];
    this.detectedSurfaces = [];
    this.showSuccess('Foto ruangan siap. Mendeteksi hotspot dari YOLO...');
    void this.detectSurfacesFromRoom(file);
  }

  private assignmentKey(surface: TargetSurface, instanceId: string | null | undefined = null): string {
    return instanceId ? `${surface}:${instanceId}` : surface;
  }

  private activeAssignmentKey(): string {
    return this.assignmentKey(this.settings.targetClass, this.activeInstanceId);
  }

  private hotspotKey(hotspot: SurfaceHotspot): string {
    return this.assignmentKey(hotspot.surface, hotspot.instanceId);
  }

  private detectResponseToHotspot(response: any, fallbackSurface: TargetSurface): SurfaceHotspot | null {
    const surface = this.normalizeDetectedSurface(
      String(response.target_class || response.detections?.target_class || fallbackSurface)
    );

    if (!surface) {
      return null;
    }

    const option = this.surfaceOptions.find((item) => item.value === surface);
    const imageWidth = Number(response.image?.width || 1);
    const imageHeight = Number(response.image?.height || 1);

    const quad =
      response.hotspot ||
      response.surface_quad ||
      response.floor_quad ||
      null;

    const polygon = this.quadToPolygon(quad);

    const center = polygon.length > 0
      ? this.getPolygonCenterRatio(polygon, imageWidth, imageHeight)
      : this.hotspotPositions[surface] || { xRatio: 0.5, yRatio: 0.5 };

    const nonzeroPixels = Number(response.detections?.mask?.nonzero_pixels || 0);
    const predictionCount = Number(response.debug?.prediction_count || 0);

    // Kalau Roboflow benar-benar tidak nemu mask untuk surface ini, skip.
    if (nonzeroPixels <= 0 && polygon.length === 0 && predictionCount <= 0) {
      return null;
    }

    return {
      surface,
      label: option?.label || surface,
      hint: `${option?.hint || surface} · Roboflow`,
      xRatio: this.clampNumber(center.xRatio, 0, 1),
      yRatio: this.clampNumber(center.yRatio, 0, 1),
      productSku: null,
      productName: null,
      productThumbnailUrl: null,
      isConfigured: false,
      confidence: predictionCount > 0 ? 1 : 0,
      polygon
    };
  }


  private quadToPolygon(quad: PerspectivePoints | null | undefined): Point2D[] {
    if (!quad) {
      return [];
    }

    const points = [
      quad.topLeft,
      quad.topRight,
      quad.bottomRight,
      quad.bottomLeft
    ];

    if (points.some((point) => !point || !Number.isFinite(Number(point.x)) || !Number.isFinite(Number(point.y)))) {
      return [];
    }

    return points.map((point) => ({
      x: Math.round(Number(point.x)),
      y: Math.round(Number(point.y))
    }));
  }

  private normalizePolygon(rawPolygon: unknown): Point2D[] {
    if (!Array.isArray(rawPolygon)) {
      return [];
    }

    return rawPolygon
      .map((point) => ({
        x: Math.round(Number((point as Point2D)?.x)),
        y: Math.round(Number((point as Point2D)?.y))
      }))
      .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y));
  }


  private getPolygonCenterRatio(
    polygon: Point2D[],
    imageWidth: number,
    imageHeight: number
  ): { xRatio: number; yRatio: number } {
    if (!polygon.length) {
      return {
        xRatio: 0.5,
        yRatio: 0.5
      };
    }

    const sum = polygon.reduce(
      (acc, point) => ({
        x: acc.x + Number(point.x || 0),
        y: acc.y + Number(point.y || 0)
      }),
      { x: 0, y: 0 }
    );

    return {
      xRatio: this.clampNumber(sum.x / polygon.length / Math.max(1, imageWidth), 0, 1),
      yRatio: this.clampNumber(sum.y / polygon.length / Math.max(1, imageHeight), 0, 1)
    };
  }

  private async detectSurfacesFromRoom(file: File): Promise<void> {
    this.isDetectingSurfaces = true;
    this.detectedSurfaceHotspots = [];
    this.detectedSurfaces = [];

    const conf = Number(this.settings.conf || 0.25);
    const imgsz = Number(this.settings.imgsz || 640);

    const surfacesToDetect: TargetSurface[] = [
      'floor',
      'wall',
      'backsplash',
      'countertop',
      'wardrobe',
      'base_cabinet',
      'wall_cabinet',
      'ceiling'
    ];

    try {
      const results = await Promise.allSettled(
        surfacesToDetect.map(async (surface) => {
          const response = await firstValueFrom(
            this.api.detectSurfaces(file, surface, conf, imgsz)
          );

          return {
            surface,
            response
          };
        })
      );

      const hotspots: SurfaceHotspot[] = [];

      for (const result of results) {
        if (result.status !== 'fulfilled') {
          console.warn('Detect surface gagal:', result.reason);
          continue;
        }

        const { surface, response } = result.value;

        if (!response || response.status !== 'success') {
          continue;
        }

        const hotspot = this.detectResponseToHotspot(response, surface);

        if (hotspot) {
          hotspots.push(hotspot);
        }

        if (surface === this.settings.targetClass) {
          const maskUrl = response.files?.mask_url || response.mask_url || null;
          const sliceUrl = response.files?.slice_url || response.slice_url || null;

          this.urls = {
            ...this.urls,
            mask: this.api.resolveBackendUrl(maskUrl),
            slice: this.api.resolveBackendUrl(sliceUrl)
          };
        }
      }

      this.detectedSurfaceHotspots = hotspots;

      if (this.detectedSurfaceHotspots.length > 0) {
        const firstHotspot =
          this.detectedSurfaceHotspots.find((item) => item.surface === 'floor') ||
          this.detectedSurfaceHotspots[0];

        this.setTargetSurface(firstHotspot.surface, firstHotspot.instanceId || null);

      }
    } catch (error) {
      console.error(error);
    } finally {
      this.isDetectingSurfaces = false;
    }
  }

  private detectionToHotspot(detection: DetectSurfaceItem, imageWidth: number, imageHeight: number): SurfaceHotspot | null {
    const surface = this.normalizeDetectedSurface(String(detection.class_name || ''));
    if (!surface) {
      return null;
    }

    const option = this.surfaceOptions.find((item) => item.value === surface);
    return {
      surface,
      label: option?.label || surface,
      hint: `${detection.class_name} · confidence ${(Number(detection.confidence || 0) * 100).toFixed(0)}%`,
      xRatio: this.clampNumber(detection.label_point.x / Math.max(1, imageWidth), 0, 1),
      yRatio: this.clampNumber(detection.label_point.y / Math.max(1, imageHeight), 0, 1),
      productSku: null,
      productName: null,
      productThumbnailUrl: null,
      isConfigured: false,
      confidence: detection.confidence,
      polygon: detection.polygon || []
    };
  }

  private normalizeDetectedSurface(className: string): TargetSurface | null {
    const normalized = String(className || '')
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '_');

    const allowed: TargetSurface[] = ['floor', 'wall', 'ceiling'];

    return allowed.includes(normalized as TargetSurface)
      ? normalized as TargetSurface
      : null;
  }

  private polygonToPerspectivePoints(polygon: Point2D[]): PerspectivePoints | null {
    if (!polygon || polygon.length < 4) {
      return null;
    }

    const points = polygon.map((point) => ({ x: Number(point.x), y: Number(point.y) }));
    const top = [...points].sort((a, b) => a.y - b.y).slice(0, Math.max(2, Math.ceil(points.length / 2)));
    const bottom = [...points].sort((a, b) => b.y - a.y).slice(0, Math.max(2, Math.ceil(points.length / 2)));

    const topLeft = top.reduce((best, point) => (point.x < best.x ? point : best), top[0]);
    const topRight = top.reduce((best, point) => (point.x > best.x ? point : best), top[0]);
    const bottomLeft = bottom.reduce((best, point) => (point.x < best.x ? point : best), bottom[0]);
    const bottomRight = bottom.reduce((best, point) => (point.x > best.x ? point : best), bottom[0]);

    return {
      topLeft: { x: Math.round(topLeft.x), y: Math.round(topLeft.y) },
      topRight: { x: Math.round(topRight.x), y: Math.round(topRight.y) },
      bottomRight: { x: Math.round(bottomRight.x), y: Math.round(bottomRight.y) },
      bottomLeft: { x: Math.round(bottomLeft.x), y: Math.round(bottomLeft.y) }
    };
  }

  private assignProductToActiveSurface(product: ProductTexture, customTextureFile: File | null): void {
    const surface = this.settings.targetClass;
    const prepared = this.preparedSurfaces[surface] ?? null;
    const instanceId = this.activeInstanceId || null;
    const assignmentKey = this.assignmentKey(surface, instanceId);
    const preparedInstance = instanceId
      ? prepared?.instances.find((item) => item.id === instanceId) ?? null
      : null;
    const hasPreparedSurfaceMap = Object.keys(this.preparedSurfaces).length > 0;

    if (hasPreparedSurfaceMap && (!prepared || !prepared.available || (!prepared.maskUrl && !prepared.instances.length))) {
      this.showError(`Mask ${this.surfaceLabel(surface)} belum tersedia dari AI. Pilih surface lain atau retry detection untuk surface ini.`);
      return;
    }

    if (instanceId && !preparedInstance) {
      this.showError(`Mask ${this.surfaceLabel(surface)} untuk titik ini belum tersedia. Klik hotspot lain atau retry detection.`);
      return;
    }

    const resolvedTextureUrl = customTextureFile
      ? product.textureUrl
      : this.api.resolveBackendUrl(product.textureUrl);

    const resolvedThumbnailUrl = this.api.resolveBackendUrl(product.thumbnailUrl) || product.thumbnailUrl;

    const productForSurface: ProductTexture = {
      ...product,
      targetClass: surface,
      textureUrl: this.safeProductImageUrl(resolvedTextureUrl || product.textureUrl),
      thumbnailUrl: this.safeProductImageUrl(resolvedThumbnailUrl),
    };

    const productPlankWidthM =
      Number(product.plankWidthM || 0) ||
      Number(product.width ? product.width / 1000 : 0) ||
      this.settings.plankWidthM;

    const productPlankLengthM =
      Number(product.plankLengthM || 0) ||
      Number(product.height ? product.height / 1000 : 0) ||
      this.settings.plankLengthM;

    const productPattern = this.normalizePatternName(
      product.defaultPattern ||
      this.settings.pattern ||
      'grid'
    ) as GenerateSettings['pattern'];

    const productTextureScale =
      Number(product.textureRepeatScale || 0) ||
      this.settings.textureScale;

    const productRotation =
      Number(product.defaultRotation ?? this.settings.rotationAngle ?? 0);

    const productOffsetX =
      Number(product.defaultOffsetX ?? this.settings.textureOffsetX ?? 0);

    const productOffsetY =
      Number(product.defaultOffsetY ?? this.settings.textureOffsetY ?? 0);

    const productGroutWidthM =
      (Number(product.defaultGroutSize || 0) > 0
        ? Number(product.defaultGroutSize) / 1000
        : 0) ||
      this.settings.groutWidthM;
    const productGroutSizeMm =
      Number(product.defaultGroutSize || 0) ||
      Number(this.settings.groutSizeMm || 0) ||
      Math.round(productGroutWidthM * 1000) ||
      5;
    const productGroutColor =
      product.groutColor ||
      this.settings.groutColor ||
      '#9E9E9E';

    let nextSettings: GenerateSettings = {
      ...this.settings,
      targetClass: surface,
      plankWidthM: productPlankWidthM,
      plankLengthM: productPlankLengthM,
      pattern: productPattern,
      textureScale: productTextureScale,
      rotationAngle: productRotation,
      textureOffsetX: productOffsetX,
      textureOffsetY: productOffsetY,
      groutWidthM: productGroutWidthM,
      groutEnabled: this.settings.groutEnabled ?? false,
      groutSizeMm: productGroutSizeMm,
      groutColor: productGroutColor,
    };

    const existingHotspot = this.detectedSurfaceHotspots.find(
      (item) => item.surface === surface && (item.instanceId || null) === instanceId
    );

    const hotspotQuad = existingHotspot?.polygon?.length
      ? this.polygonToPerspectivePoints(existingHotspot.polygon)
      : null;
    const preparedQuad = preparedInstance?.quad || prepared?.quad || prepared?.instances?.[0]?.quad || null;

    const surfacePerspectivePoints = this.shouldUsePerspectiveProjection(surface)
      ? (preparedQuad || hotspotQuad || nextSettings.perspectivePoints || null)
      : null;

    nextSettings = {
      ...nextSettings,
      perspectivePoints: surfacePerspectivePoints
    };

    const assignment: SurfaceAssignment = {
      surface,
      instanceId,
      product: productForSurface,
      settings: nextSettings,
      textureUrl: resolvedTextureUrl || product.textureUrl,
      customTextureFile,
      floorQuad: this.shouldUsePerspectiveProjection(surface) ? nextSettings.perspectivePoints : null,
      maskUrl: preparedInstance?.maskUrl || prepared?.maskUrl || this.urls.mask,
      sliceUrl: preparedInstance?.sliceUrl || prepared?.sliceUrl || this.urls.slice,
    };

    this.surfaceAssignments = {
      ...this.surfaceAssignments,
      [assignmentKey]: assignment,
    };

    this.settings = nextSettings;
    this.selectedProduct = productForSurface;
    this.selectedProductTextureFile = customTextureFile;

    this.urls = {
      ...this.urls,
      texture: resolvedTextureUrl || product.textureUrl,
      result: null,
    };

    this.viewerMode = 'preview';
    this.rebuildPreviewLayers();

    this.detectedSurfaceHotspots = this.detectedSurfaceHotspots.map((hotspot) => {
      const matchesAssignment = this.isMultiInstanceSurface(surface) && !instanceId
        ? hotspot.surface === surface
        : hotspot.surface === surface && (hotspot.instanceId || null) === instanceId;

      if (!matchesAssignment) {
        return hotspot;
      }

      return {
        ...hotspot,
        productSku: product.sku,
        productName: product.name,
        productThumbnailUrl: resolvedThumbnailUrl,
        isConfigured: true,
      };
    });

    console.log('preview state after product selected', {
      viewerMode: this.viewerMode,
      previewTextureUrl: this.selectedProductTextureUrl,
      urlsTexture: this.urls.texture,
      urlsResult: this.urls.result,
      urlsMask: this.urls.mask,
      urlsSlice: this.urls.slice,
      perspectivePoints: this.settings.perspectivePoints,
    });

    this.showSuccess(`${product.sku} dipasang ke ${this.surfaceLabel(surface)}. Preview realtime aktif.`);

    // Jangan paksa ulang prepare surface dulu.
    // Mask/slice floor sudah datang dari prepare-room.
    // Kalau nanti mau detect surface selain floor, baru panggil manual.
    // void this.prepareSurfaceAssets(surface);
  }


  private statusTimer: ReturnType<typeof setTimeout> | null = null;

private showSuccess(message: string): void {
  if (this.shouldSuppressSuccessMessage(message)) {
    this.successMessage = '';
    return;
  }

  this.successMessage = message;
  this.errorMessage = '';
  this.autoHideStatus();
}

private shouldSuppressSuccessMessage(message: string): boolean {
  const normalized = String(message || '').toLowerCase();

  return (
    normalized.startsWith('foto siap') ||
    normalized.startsWith('foto ruangan siap') ||
    normalized.includes('klik titik lalu pilih produk') ||
    normalized.includes('siap dari manifest') ||
    normalized.includes('tidak menjalankan analyze ulang') ||
    (normalized.includes('terdeteksi') && normalized.includes('surface'))
  );
}

private showError(message: string): void {
  this.errorMessage = message;
  this.successMessage = '';
  this.autoHideStatus();
}

private autoHideStatus(): void {
  if (this.statusTimer) {
    clearTimeout(this.statusTimer);
  }

  this.statusTimer = setTimeout(() => {
    this.errorMessage = '';
    this.successMessage = '';
    this.statusTimer = null;
  }, 5000);
}
  private async prepareSurfaceAssets(surface: TargetSurface): Promise<void> {
    if (!this.selectedRoomFile) {
      return;
    }

    const key = surface === this.settings.targetClass ? this.activeAssignmentKey() : this.assignmentKey(surface, null);
    const assignment = this.surfaceAssignments[key] ?? null;
    if (!assignment) {
      return;
    }

    this.isPreparingSurface = true;
    try {
      const response = await firstValueFrom(
        this.api.prepareSurface(this.selectedRoomFile, {
          ...assignment.settings,
          targetClass: surface
        })
      ) as GenerateResponse;

      if (response.status !== 'success') {
        this.showSuccess(`${this.surfaceLabel(surface)} siap preview, tapi mask belum tersedia.`)
        return;
      }

      this.applyGenerateResponseToAssignment(assignment, response);

      if (surface === this.settings.targetClass) {
        this.urls = {
          ...this.urls,
          mask: this.api.resolveBackendUrl(response.mask_url),
          slice: this.api.resolveBackendUrl(response.slice_url)
        };
      }
      this.showSuccess(`${this.surfaceLabel(surface)} siap. Preview realtime bisa diatur dari scale, rotasi, offset, dan pola.`)
      
    } catch (error) {
      console.error(error);
      this.showSuccess(`${this.surfaceLabel(surface)} siap preview. Mask backend belum tersedia, pakai quad hotspot dulu.`)
    } finally {
      this.isPreparingSurface = false;
    }
  }



  private settingsForSurface(surface: TargetSurface): GenerateSettings {
    const assignment = this.assignmentFor(surface);
    if (assignment) {
      return {
        ...assignment.settings,
        targetClass: surface,
        pattern: this.isKisiOnlySurface(surface) ? 'straight' : assignment.settings.pattern
      };
    }

    return {
      ...this.settings,
      targetClass: surface,
      perspectivePoints: this.shouldUsePerspectiveProjection(surface) ? this.settings.perspectivePoints : null,
      pattern: this.isKisiOnlySurface(surface) ? 'straight' : this.settings.pattern
    };
  }

  private isKisiOnlySurface(surface: TargetSurface): boolean {
    return surface === 'wall';
  }

  private isMultiInstanceSurface(surface: TargetSurface): boolean {
    return surface === 'wall' || surface === 'ceiling';
  }

  private async prepareRoomFromTemplate(room: RoomSample): Promise<void> {
    this.isPreparingSurface = true;
    this.errorMessage = '';
    this.successMessage = '';

    this.activeRoomSource = 'template';
    this.selectedTemplateId = String(room.id);
    this.selectedRoomFile = null;
    this.preparedImageId = null;
    this.preparedFloorQuad = null;
    this.detectedSurfaceHotspots = [];
    this.detectedSurfaces = [];
    this.surfaceAssignments = {};
    this.activeInstanceId = null;
    this.previewLayers = [];
    this.lastResponse = null;

    const fallbackImageUrl = this.normalizeImageUrl(room.imageUrl);

    this.selectedRoomUrl = fallbackImageUrl;
    this.urls = {
      ...this.urls,
      original: fallbackImageUrl,
      result: null,
      mask: null,
      slice: null,
    };

    try {
      const response = await firstValueFrom(this.api.loadTemplateRoom(room.id));

      if (!response || response.status !== 'success') {
        throw new Error(response?.message || 'Template room belum siap.');
      }

      const manifestImageUrl = this.api.resolveBackendUrl(response.image?.url || response.roomTexture) || fallbackImageUrl;
      this.applyPreparedRoomResponse(response, manifestImageUrl);
      this.selectedRoomUrl = manifestImageUrl;
      this.urls = {
        ...this.urls,
        original: manifestImageUrl,
      };
      this.viewerMode = 'original';
      this.openWorkspacePanelsForViewport();

    } catch (error) {
      console.error(error);
      this.viewerMode = 'original';
      this.openWorkspacePanelsForViewport();
      this.showError(this.formatError(error) || 'Template belum punya manifest, memakai foto original dulu.');
    } finally {
      this.isPreparingSurface = false;
    }
  }

  private async prepareRoomFromFile(file: File): Promise<void> {
    this.isPreparingSurface = true;
    this.errorMessage = '';
    this.successMessage = '';

    this.activeRoomSource = 'upload';
    this.selectedTemplateId = null;
    this.selectedRoomFile = file;
    this.preparedImageId = null;
    this.preparedFloorQuad = null;
    this.detectedSurfaceHotspots = [];
    this.detectedSurfaces = [];
    this.surfaceAssignments = {};
    this.activeInstanceId = null;
    this.previewLayers = [];

    const localUrl = URL.createObjectURL(file);
    this.objectUrls.add(localUrl);

    this.selectedRoomUrl = localUrl;
    this.urls = {
      ...this.urls,
      original: localUrl,
      result: null,
      mask: null,
      slice: null,
    };

    try {
      const response = await firstValueFrom(
        this.api.prepareRoom(
          file,
          Number(this.settings.conf || 0.25),
          Number(this.settings.imgsz || 640)
        )
      );

      if (!response || response.status !== 'success') {
        this.showSuccess('Foto siap, tapi floor quad belum berhasil terdeteksi.');
        return;
      }

      this.applyPreparedRoomResponse(response, localUrl);
      this.openWorkspacePanelsForViewport();
    } catch (error) {
      console.error(error);
      this.showSuccess('Foto siap, tapi prepare floor gagal.');
    } finally {
      this.isPreparingSurface = false;
    }
  }

  private trackLocationUsage(area: string, type: string, action: string): void {
    this.locationUsage.track({
      company_id: this.resolveCurrentCompanyId(),
      area,
      type,
      action
    }).subscribe();
  }

  private resolveCurrentCompanyId(): number {
    const queryCompanyId = this.readCompanyIdFromQuery();

    if (queryCompanyId) {
      return queryCompanyId;
    }

    const directStorageKeys = [
      'company_id',
      'companyId',
      'current_company_id',
      'currentCompanyId'
    ];

    for (const key of directStorageKeys) {
      const value = this.toPositiveNumber(localStorage.getItem(key));

      if (value) {
        return value;
      }
    }

    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const value = this.toPositiveNumber(user.company_id || user.companyId || user.company?.id);

      if (value) {
        return value;
      }
    } catch {
      // ignore malformed storage
    }

    return 1;
  }

  private readCompanyIdFromQuery(): number | null {
    try {
      const params = new URLSearchParams(window.location.search);

      return this.toPositiveNumber(
        params.get('company_id') ||
        params.get('companyId')
      );
    } catch {
      return null;
    }
  }

  private toPositiveNumber(value: unknown): number | null {
    const numberValue = Number(value);

    return Number.isInteger(numberValue) && numberValue > 0 ? numberValue : null;
  }

syncActiveAssignmentSettings(): void {
    const surface = this.settings.targetClass;
    const activeKey = this.activeAssignmentKey();
    const fallbackSurfaceKey = this.assignmentKey(surface, null);
    const key = this.isMultiInstanceSurface(surface) && this.activeInstanceId && !this.surfaceAssignments[activeKey]
      ? fallbackSurfaceKey
      : activeKey;
    const assignment = this.surfaceAssignments[key] ?? null;
    if (!assignment) {
      return;
    }

    const nextSettings: GenerateSettings = {
      ...this.settings,
      targetClass: surface,
      perspectivePoints: this.shouldUsePerspectiveProjection(surface) ? this.settings.perspectivePoints : null,
      // Dinding cuma pakai raw texture kisi; pola lantai tidak diterapkan.
      pattern: this.isKisiOnlySurface(surface) ? 'straight' : this.settings.pattern
    };

    this.surfaceAssignments = {
      ...this.surfaceAssignments,
      [key]: {
        ...assignment,
        settings: nextSettings
      }
    };

    this.rebuildPreviewLayers();
  }

updatePattern(pattern: string): void {
  this.patternDropdownOpen = false;
  const normalizedPattern = this.normalizePatternName(pattern);
  const nextPattern = normalizedPattern === 'random'
    ? this.pickRandomPatternOption()
    : normalizedPattern;

  if (this.isKisiOnlySurface(this.settings.targetClass)) {
    this.settings = {
      ...this.settings,
      pattern: 'straight' as any
    };

    this.viewerMode = 'preview';
    this.syncActiveAssignmentSettings();
    this.showSuccess('Dinding memakai motif kisi produk langsung. Pola bata / herringbone tidak diterapkan untuk dinding.');
    return;
  }

  this.settings = {
    ...this.settings,
    pattern: nextPattern as any
  };

  this.viewerMode = 'preview';
  this.syncActiveAssignmentSettings();
}

private pickRandomPatternOption(): string {
  const currentPattern = this.normalizePatternName(this.settings.pattern);
  const candidates = this.activePatternOptions
    .map((option) => this.normalizePatternName(option.value))
    .filter((value) => value && value !== 'random');
  const uniqueCandidates = Array.from(new Set(candidates));
  const differentCandidates = uniqueCandidates.filter((value) => value !== currentPattern);
  const choices = differentCandidates.length > 0 ? differentCandidates : uniqueCandidates;

  if (choices.length === 0) {
    return currentPattern === 'random' ? 'grid' : currentPattern;
  }

  return choices[Math.floor(Math.random() * choices.length)];
}

updateRoomWidth(value: number | string): void {
  const roomWidthM = this.clampNumber(Number(value) || this.settings.roomWidthM || 4, 1.5, 20);
  this.settings = {
    ...this.settings,
    roomWidthM
  };
  this.syncActiveAssignmentSettings();
}

updateRoomDepth(value: number | string): void {
  const roomDepthM = this.clampNumber(Number(value) || this.settings.roomDepthM || 5, 1.5, 30);
  this.settings = {
    ...this.settings,
    roomDepthM
  };
  this.syncActiveAssignmentSettings();
}

updateRenderDetail(value: number | string): void {
  const pixelsPerMeter = Math.round(this.clampNumber(Number(value) || this.settings.pixelsPerMeter || 900, 600, 1400));
  this.settings = {
    ...this.settings,
    pixelsPerMeter
  };
  this.syncActiveAssignmentSettings();
}


openMobileAdjustMode(mode: MobileAdjustMode): void {
  if (!this.activeProduct) {
    return;
  }

  if (mode === 'compare') {
    this.activateMobileCompare();
    return;
  }

  this.mobileCompareControlsOpen = false;
  this.exitCompareMode();
  this.mobileAdjustMode = mode;
  this.catalogOpen = false;
  this.adjustmentOpen = true;
}

backToUpload(): void {
  this.urls = {
    original: null,
    texture: null,
    result: null,
    mask: null,
    slice: null
  };
  this.selectedRoomFile = null;
  this.selectedRoomUrl = null;
  this.activeRoomSource = null;
  this.selectedTemplateId = null;
  this.preparedImageId = null;
  this.preparedFloorQuad = null;
  this.preparedSurfaces = {};
  this.detectedSurfaceHotspots = [];
  this.detectedSurfaces = [];
  this.surfaceAssignments = {};
  this.activeInstanceId = null;
  this.previewLayers = [];
  this.catalogOpen = true;
  this.adjustmentOpen = false;
  this.usedProductsOpen = false;
  this.mobileAdjustMode = 'main';
  this.mobileCatalogStep = 'category';
  this.selectedCategory = 'all';
  this.selectedSeries = 'all';
  this.catalogSearchKeyword = '';
  this.viewerMode = 'original';
}

updateTextureOffsetX(value: number | string): void {
  this.settings = {
    ...this.settings,
    textureOffsetX: Number(value) || 0
  };

  this.viewerMode = 'preview';
  this.syncActiveAssignmentSettings();
}

updateTextureOffsetY(value: number | string): void {
  this.settings = {
    ...this.settings,
    textureOffsetY: Number(value) || 0
  };

  this.viewerMode = 'preview';
  this.syncActiveAssignmentSettings();
}

randomizeLayout(): void {
  this.settings = {
    ...this.settings,
    textureOffsetX: Number((((this.settings.textureOffsetX || 0) + 0.137) % 1).toFixed(3)),
    textureOffsetY: Number((((this.settings.textureOffsetY || 0) + 0.271) % 1).toFixed(3)),
    rotationAngle: Number((((this.settings.rotationAngle || 0) + 90) % 180).toFixed(0))
  };

  this.viewerMode = 'preview';
  this.syncActiveAssignmentSettings();
}

updateTextureRotation(value: number | string): void {
  this.settings = {
    ...this.settings,
    rotationAngle: Number(value) || 0
  };

  this.viewerMode = 'preview';
  this.syncActiveAssignmentSettings();
}

togglePatternDropdown(event?: Event): void {
  event?.stopPropagation();
  this.patternDropdownOpen = !this.patternDropdownOpen;
}

selectPatternOption(pattern: string, event?: Event): void {
  event?.preventDefault();
  event?.stopPropagation();
  const normalizedPattern = this.normalizePatternName(pattern);
  const now = Date.now();

  if (normalizedPattern === 'random') {
    if (event?.type === 'click' && now - this.lastRandomPatternSelectAt < 350) {
      return;
    }

    this.lastRandomPatternSelectAt = now;
  }

  if (normalizedPattern === 'mix') {
    if (event?.type === 'click' && now - this.lastMixPatternSelectAt < 350) {
      return;
    }

    this.lastMixPatternSelectAt = now;
    this.patternDropdownOpen = false;
    this.updatePattern(normalizedPattern);
    this.randomizeLayout();
    return;
  }

  this.patternDropdownOpen = false;
  this.updatePattern(normalizedPattern);
}

updateGroutWidth(value: number | string): void {
  const groutWidthMm = this.clampNumber(Number(value) || 5, 0, 80);
  this.settings = {
    ...this.settings,
    groutWidthM: groutWidthMm / 1000,
    groutSizeMm: groutWidthMm,
    groutEnabled: groutWidthMm > 0 ? this.settings.groutEnabled : false
  };

  this.viewerMode = 'preview';
  this.syncActiveAssignmentSettings();
}

updateGroutColor(value: string): void {
  this.settings = {
    ...this.settings,
    groutColor: value || '#9E9E9E'
  };

  this.viewerMode = 'preview';
  this.syncActiveAssignmentSettings();
}

updateGroutEnabled(value: boolean): void {
  const sizeMm = Number(this.settings.groutSizeMm || (this.settings.groutWidthM || 0) * 1000 || 5);
  this.settings = {
    ...this.settings,
    groutEnabled: !!value,
    groutSizeMm: sizeMm,
    groutWidthM: sizeMm / 1000
  };

  this.viewerMode = 'preview';
  this.syncActiveAssignmentSettings();
}

nudgeTextureOffset(deltaX: number, deltaY: number): void {
  const currentX = Number(this.settings.textureOffsetX ?? 0);
  const currentY = Number(this.settings.textureOffsetY ?? 0);

  this.settings = {
    ...this.settings,
    textureOffsetX: Number((currentX - deltaX).toFixed(3)),
    textureOffsetY: Number((currentY + deltaY).toFixed(3))
  };

  this.viewerMode = 'preview';
  this.syncActiveAssignmentSettings();
}

resetTextureOffset(): void {
  this.settings = {
    ...this.settings,
    textureOffsetX: 0,
    textureOffsetY: 0
  };

  this.viewerMode = 'preview';
  this.syncActiveAssignmentSettings();
}

resetPatternControls(): void {
  this.settings = {
    ...this.settings,
    textureScale: 1,
    textureOffsetX: 0,
    textureOffsetY: 0,
    rotationAngle: 0,
    pattern: 'grid'
  };

  this.viewerMode = 'preview';
  this.syncActiveAssignmentSettings();
}
  private applyGenerateResponseToAssignment(assignment: SurfaceAssignment, response: GenerateResponse): void {
    const key = this.assignmentKey(assignment.surface, assignment.instanceId);
    if (!assignment) {
      return;
    }

    const room = response.room_estimation || null;
    const areaM2 = Number(room?.surface_area_m2 || room?.room_area_m2 || 0) || undefined;
    const aiLength = Number(room?.room_depth_m || 0) || Number(room?.['length_m'] || 0) || undefined;
    const aiWidth = Number(room?.room_width_m || 0) || Number(room?.['width_m'] || 0) || undefined;
    const resolvedQuad = assignment.settings.perspectivePoints || assignment.floorQuad || response.floor_quad || null;

    this.surfaceAssignments = {
      ...this.surfaceAssignments,
      [key]: {
        ...assignment,
        aiLength,
        aiWidth,
        areaM2,
        floorQuad: resolvedQuad,
        maskUrl: this.api.resolveBackendUrl(response.mask_url),
        sliceUrl: this.api.resolveBackendUrl(response.slice_url),
        settings: assignment.surface === 'floor' && resolvedQuad
          ? { ...assignment.settings, perspectivePoints: resolvedQuad, targetClass: assignment.surface }
          : assignment.settings
      }
    };

    this.rebuildPreviewLayers();

    if (assignment.surface === this.settings.targetClass && assignment.surface === 'floor' && resolvedQuad) {
      this.settings = {
        ...this.settings,
        perspectivePoints: resolvedQuad
      };
    }
  }


  private applyPreparedRoomResponse(response: any, localUrl: string): void {
    this.preparedImageId = response.image_id || null;
    this.preparedFloorQuad = response.floor_quad || null;
    this.preparedSurfaces = this.mapPreparedSurfaces(response);

    const backendImageUrl = this.api.resolveBackendUrl(response.image?.url);
    const floorPrepared = this.preparedSurfaces['floor'] ?? null;
    const activePrepared = this.preparedSurfaces[this.settings.targetClass] ?? null;
    const activeInstance = this.activeInstanceId
      ? activePrepared?.instances.find((item) => item.id === this.activeInstanceId) ?? null
      : null;

    this.urls = {
      ...this.urls,
      original: backendImageUrl || localUrl,
      result: null,
      mask: activeInstance?.maskUrl || activePrepared?.maskUrl || floorPrepared?.maskUrl || null,
      slice: activeInstance?.sliceUrl || activePrepared?.sliceUrl || floorPrepared?.sliceUrl || null,
    };

      this.selectedRoomUrl = this.urls.original;

      const availableSurfaces = this.surfaceOrder
      .map((surface) => this.preparedSurfaces[surface] ?? null)
      .filter((surface): surface is PreparedSurfaceView => !!surface && surface.available);

    const imageWidth = Number(response.image?.width || 1);
    const imageHeight = Number(response.image?.height || 1);
    const responseHotspots = this.responseHotspotsToSurfaceHotspots(response.hotspots || [], imageWidth, imageHeight);

    this.detectedSurfaceHotspots = responseHotspots.length > 0
      ? responseHotspots
      : availableSurfaces.flatMap((surface) => this.preparedSurfaceToHotspots(surface, imageWidth, imageHeight));

    const firstHotspot = this.chooseInitialHotspotForProducts(this.detectedSurfaceHotspots);

    if (firstHotspot) {
      const firstPrepared = this.preparedSurfaces[firstHotspot.surface] ?? null;
      const firstInstance = firstHotspot.instanceId
        ? firstPrepared?.instances.find((item) => item.id === firstHotspot.instanceId) ?? null
        : null;
      const firstQuad = firstInstance?.quad || firstPrepared?.quad || firstPrepared?.instances[0]?.quad || this.settings.perspectivePoints;

      this.activeInstanceId = firstHotspot.instanceId || null;
      this.settings = {
        ...this.settings,
        targetClass: firstHotspot.surface,
        perspectivePoints: firstQuad
      };

      this.selectedProduct = null;
      this.selectedProductTextureFile = null;
      this.urls.texture = null;
      this.selectedCategory = 'all';
      this.selectedSeries = 'all';
      this.catalogSearchKeyword = '';
      this.mobileCatalogStep = 'category';
      this.urls.mask = firstInstance?.maskUrl || firstPrepared?.maskUrl || null;
      this.urls.slice = firstInstance?.sliceUrl || firstPrepared?.sliceUrl || null;

      const labels = availableSurfaces
        .map((surface) => this.surfaceLabel(surface.surface))
        .join(', ');

    } else {
      this.showSuccess('Foto siap, tapi belum ada surface valid dari AI.');
    }

    this.rebuildPreviewLayers();
  }

  private openWorkspacePanelsForViewport(): void {
    const isMobileViewport = typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;

    this.catalogOpen = isMobileViewport;
    this.adjustmentOpen = !isMobileViewport && !!this.activeProduct;
    this.usedProductsOpen = false;
    this.mobileAdjustMode = 'main';

    if (this.catalogOpen) {
      this.mobileCatalogStep = 'category';
      this.selectedCategory = 'all';
      this.selectedSeries = 'all';
      this.catalogSearchKeyword = '';
    }

    if (typeof window !== 'undefined') {
      window.requestAnimationFrame(() => window.scrollTo({ top: 0, left: 0, behavior: 'auto' }));
    }
  }

  private chooseInitialHotspotForProducts(hotspots: SurfaceHotspot[]): SurfaceHotspot | null {
    if (!hotspots.length) {
      return null;
    }

    const preferredOrder: TargetSurface[] = ['floor', 'wall', 'ceiling'];

    for (const surface of preferredOrder) {
      const hasProduct = this.products.length === 0 || !!this.findFirstProductForSurface(surface);

      if (!hasProduct) {
        continue;
      }

      const hotspot = hotspots.find((item) => item.surface === surface);

      if (hotspot) {
        return hotspot;
      }
    }

    return (
      hotspots.find((item) => item.surface === 'floor') ||
      hotspots.find((item) => item.surface === 'wall') ||
      hotspots[0] ||
      null
    );
  }

  private responseHotspotsToSurfaceHotspots(rawHotspots: any[], imageWidth: number, imageHeight: number): SurfaceHotspot[] {
    if (!Array.isArray(rawHotspots)) {
      return [];
    }

    const allowed = new Set(this.surfaceOptions.map((item) => item.value));

    return rawHotspots
      .map((item) => {
        const surface = String(item.surface || item.target_class || '').toLowerCase() as TargetSurface;

        if (!allowed.has(surface)) {
          return null;
        }

        const instanceId = item.instance_id || item.instanceId || null;
        const prepared = this.preparedSurfaces[surface] ?? null;
        const instance = instanceId
          ? prepared?.instances.find((entry) => entry.id === instanceId) ?? null
          : prepared?.instances[0] ?? null;
        const xRatio = Number.isFinite(Number(item.xRatio))
          ? Number(item.xRatio)
          : Number(item.x || 0) / Math.max(imageWidth, 1);
        const yRatio = Number.isFinite(Number(item.yRatio))
          ? Number(item.yRatio)
          : Number(item.y || 0) / Math.max(imageHeight, 1);

        return {
          surface,
          instanceId,
          label: item.label || instance?.label || this.surfaceLabel(surface),
          hint: item.hint || `${item.label || instance?.label || this.surfaceLabel(surface)} - template`,
          xRatio: this.clampNumber(xRatio, 0, 1),
          yRatio: this.clampNumber(yRatio, 0, 1),
          productSku: null,
          productName: null,
          productThumbnailUrl: null,
          isConfigured: !!(
            this.surfaceAssignments[this.assignmentKey(surface, instanceId)] ||
            (this.isMultiInstanceSurface(surface) ? this.surfaceAssignments[this.assignmentKey(surface, null)] : null)
          ),
          confidence: Number(item.confidence ?? instance?.confidence ?? 1),
          polygon: this.normalizePolygon(item.polygon || instance?.polygon || null),
          estimatedSize: item.estimated_size || instance?.estimatedSize || null,
        } as SurfaceHotspot;
      })
      .filter((item): item is SurfaceHotspot => !!item);
  }

  private ensureSelectedProductForActiveSurface(): void {
    const activeAssignment = this.activeSurfaceAssignment;

    if (activeAssignment) {
      this.selectedProduct = activeAssignment.product;
      this.urls.texture = activeAssignment.textureUrl ?? activeAssignment.product.textureUrl ?? null;
      return;
    }

    this.selectedProduct = null;
    this.urls.texture = null;

    const fallbackHotspot = this.chooseInitialHotspotForProducts(this.detectedSurfaceHotspots);

    if (!fallbackHotspot) {
      return;
    }

    const prepared = this.preparedSurfaces[fallbackHotspot.surface] ?? null;
    const preparedInstance = fallbackHotspot.instanceId
      ? prepared?.instances.find((item) => item.id === fallbackHotspot.instanceId) ?? null
      : null;

    this.activeInstanceId = fallbackHotspot.instanceId || null;
    this.settings = {
      ...this.settings,
      targetClass: fallbackHotspot.surface,
      perspectivePoints: preparedInstance?.quad || prepared?.quad || this.settings.perspectivePoints
    };

    this.selectedCategory = 'all';
    this.selectedSeries = 'all';
    this.mobileCatalogStep = 'category';
  }

  private mapPreparedSurfaces(response: any): Partial<Record<TargetSurface, PreparedSurfaceView>> {
    const result: Partial<Record<TargetSurface, PreparedSurfaceView>> = {};
    const rawSurfaces = response?.surfaces || {};

    for (const option of this.surfaceOptions) {
      const surface = option.value;
      const raw = rawSurfaces?.[surface];

      if (!raw) {
        continue;
      }

      const available = raw.available !== false && !!(raw.mask_url || raw.maskUrl || raw.instances?.length);
      const instances = this.parseSurfaceInstances(surface, raw.instances || []);

      const roomAssets = raw.room_assets || raw.roomAssets || {};
      const renderMaskUrl = raw.visibleMaskUrl || raw.visible_mask_url || raw.render_mask_url || raw.renderMaskUrl || raw.mask_texture_url || raw.maskTexture || roomAssets.maskTexture || raw.mask_url || raw.maskUrl || null;
      const lightTextureUrl = raw.light_texture_url || raw.lightTextureUrl || raw.lightTexture || roomAssets.lightTexture || null;
      const hardMaskUrl = raw.hard_mask_url || raw.hardMaskUrl || raw.hardMaskTexture || roomAssets.hardMaskTexture || null;
      const occlusionMaskUrl = raw.occlusion_mask_url || raw.occlusionMaskUrl || raw.occlusionTexture || roomAssets.occlusionTexture || null;

      result[surface] = {
        surface,
        available,
        reason: raw.reason || null,
        source: raw.source || null,
        quality: raw.quality || null,
        isEstimated: !!raw.is_estimated || !!raw.isEstimated,
        maskUrl: this.api.resolveBackendUrl(renderMaskUrl),
        sliceUrl: this.api.resolveBackendUrl(raw.slice_url || raw.sliceUrl || null),
        lightTextureUrl: this.api.resolveBackendUrl(lightTextureUrl),
        hardMaskUrl: this.api.resolveBackendUrl(hardMaskUrl),
        occlusionMaskUrl: this.api.resolveBackendUrl(occlusionMaskUrl),
        quad: raw.quad || raw.surface_quad || raw.perspective_quad || raw.floor_quad || roomAssets.floor_quad || instances[0]?.quad || null,
        instances
      };
    }

    return result;
  }

  private parseSurfaceInstances(surface: TargetSurface, rawInstances: any[]): PreparedSurfaceInstanceView[] {
    if (!Array.isArray(rawInstances)) {
      return [];
    }

    return rawInstances
      .map((item, index) => {
        const maskUrl = this.api.resolveBackendUrl(item.visibleMaskUrl || item.visible_mask_url || item.render_mask_url || item.renderMaskUrl || item.mask_texture_url || item.maskTexture || item.mask_url || item.maskUrl || null);
        const sliceUrl = this.api.resolveBackendUrl(item.slice_url || item.sliceUrl || null);
        const lightTextureUrl = this.api.resolveBackendUrl(item.light_texture_url || item.lightTextureUrl || item.lightTexture || null);
        const hardMaskUrl = this.api.resolveBackendUrl(item.hard_mask_url || item.hardMaskUrl || item.hardMaskTexture || null);
        const occlusionMaskUrl = this.api.resolveBackendUrl(item.occlusion_mask_url || item.occlusionMaskUrl || item.occlusionTexture || null);
        const quad = item.quad || item.surface_quad || item.perspective_quad || null;
        const bbox = this.normalizeSurfaceBBox(item.bbox || item.raw_bbox || null);
        const polygon = this.normalizePolygon(item.polygon || item.contour || null);

        if (!maskUrl || (!quad && !bbox && polygon.length === 0)) {
          return null;
        }

        return {
          id: String(item.id || `${surface}_${index}`),
          surface,
          label: item.label || (surface === 'wall' ? `Wall ${index + 1}` : this.surfaceLabel(surface)),
          wallRole: item.wall_role || item.wallRole || null,
          maskUrl,
          sliceUrl,
          lightTextureUrl,
          hardMaskUrl,
          occlusionMaskUrl,
          quad,
          bbox,
          polygon,
          confidence: Number(item.confidence ?? item.score ?? 1),
          source: item.source || item.origin || (String(maskUrl).includes('/uploads/room-templates/') ? 'template_room' : null),
          quality: item.quality || null,
          nonzeroPixels: Number(item.nonzero_pixels ?? item.nonzeroPixels ?? 0),
          areaRatio: Number(item.area_ratio ?? item.areaRatio ?? 1),
          estimatedSize: item.estimated_size || item.estimatedSize || null
        } as PreparedSurfaceInstanceView;
      })
      .filter((item): item is PreparedSurfaceInstanceView => !!item);
  }

  private preparedSurfaceToHotspots(
    prepared: PreparedSurfaceView,
    imageWidth: number,
    imageHeight: number
  ): SurfaceHotspot[] {
    const option = this.surfaceOptions.find((item) => item.value === prepared.surface);

    if (this.isMultiInstanceSurface(prepared.surface) && prepared.instances.length > 0) {
      return prepared.instances.map((instance, index) => {
        const polygon = instance.polygon?.length ? instance.polygon : this.quadToPolygon(instance.quad);
        const center = polygon.length
          ? this.getPolygonCenterRatio(polygon, imageWidth, imageHeight)
          : this.hotspotPositions[prepared.surface] || { xRatio: 0.5, yRatio: 0.5 };
        const label = instance.label || (prepared.surface === 'wall' ? `Wall ${index + 1}` : `${this.surfaceLabel(prepared.surface)} ${index + 1}`);

        return {
          surface: prepared.surface,
          instanceId: instance.id,
          label,
          hint: `${label} - ${instance.source || prepared.source || 'prepared'}`,
          xRatio: this.clampNumber(center.xRatio, 0, 1),
          yRatio: this.clampNumber(center.yRatio, 0, 1),
          productSku: null,
          productName: null,
          productThumbnailUrl: null,
          isConfigured: !!(
            this.surfaceAssignments[this.assignmentKey(prepared.surface, instance.id)] ||
            this.surfaceAssignments[this.assignmentKey(prepared.surface, null)]
          ),
          confidence: instance.confidence || 1,
          polygon,
          estimatedSize: instance.estimatedSize || null
        };
      });
    }

    const quad = prepared.quad || prepared.instances[0]?.quad || null;
    const polygon = prepared.instances[0]?.polygon?.length
      ? prepared.instances[0].polygon
      : this.quadToPolygon(quad);
    const center = polygon.length
      ? this.getPolygonCenterRatio(polygon, imageWidth, imageHeight)
      : this.hotspotPositions[prepared.surface] || { xRatio: 0.5, yRatio: 0.5 };
    const instanceId = prepared.instances[0]?.id || null;

    return [{
      surface: prepared.surface,
      instanceId,
      label: option?.label || this.surfaceLabel(prepared.surface),
      hint: `${option?.hint || this.surfaceLabel(prepared.surface)} - ${prepared.source || 'prepared'}`,
      xRatio: this.clampNumber(center.xRatio, 0, 1),
      yRatio: this.clampNumber(center.yRatio, 0, 1),
      productSku: null,
      productName: null,
      productThumbnailUrl: null,
      isConfigured: !!this.surfaceAssignments[this.assignmentKey(prepared.surface, instanceId)],
      confidence: prepared.instances[0]?.confidence || 1,
      polygon,
      estimatedSize: prepared.instances[0]?.estimatedSize || null
    }];
  }

  private preparedSurfaceToHotspot(
    prepared: PreparedSurfaceView,
    imageWidth: number,
    imageHeight: number
  ): SurfaceHotspot {
    const option = this.surfaceOptions.find((item) => item.value === prepared.surface);
    const quad = prepared.quad || prepared.instances[0]?.quad || null;
    const polygon = prepared.instances[0]?.polygon?.length
      ? prepared.instances[0].polygon
      : this.quadToPolygon(quad);
    const center = polygon.length
      ? this.getPolygonCenterRatio(polygon, imageWidth, imageHeight)
      : this.hotspotPositions[prepared.surface] || { xRatio: 0.5, yRatio: 0.5 };

    return {
      surface: prepared.surface,
      label: option?.label || this.surfaceLabel(prepared.surface),
      hint: `${option?.hint || this.surfaceLabel(prepared.surface)} · ${prepared.source || 'prepared'}`,
      xRatio: this.clampNumber(center.xRatio, 0, 1),
      yRatio: this.clampNumber(center.yRatio, 0, 1),
      productSku: null,
      productName: null,
      productThumbnailUrl: null,
      isConfigured: !!this.assignmentFor(prepared.surface),
      confidence: prepared.instances[0]?.confidence || 1,
      polygon
    };
  }

  private rebuildPreviewLayers(): void {
    const layers: PreviewLayerView[] = [];

    for (const assignment of this.getOrderedAssignments()) {
      const prepared = this.preparedSurfaces[assignment.surface] ?? null;

      if (!prepared || !prepared.available) {
        continue;
      }

      const textureUrl = assignment.textureUrl || assignment.product.textureUrl;

      if (!textureUrl) {
        continue;
      }

      const needsPerspectiveQuad = this.shouldUsePerspectiveProjection(assignment.surface);
      const instances = assignment.instanceId
        ? prepared.instances.filter((instance) => instance.id === assignment.instanceId)
        : prepared.instances.length > 0
        ? prepared.instances
        : [{
            id: `${assignment.surface}_composite`,
            surface: assignment.surface,
            maskUrl: prepared.maskUrl,
            sliceUrl: prepared.sliceUrl,
            lightTextureUrl: prepared.lightTextureUrl || null,
            quad: needsPerspectiveQuad
              ? (prepared.quad || assignment.settings.perspectivePoints || null)
              : (prepared.quad || null),
            bbox: null,
          } as PreparedSurfaceInstanceView];

      for (const instance of instances) {
        if (!instance.maskUrl || this.shouldSkipPreviewInstance(assignment.surface, instance)) {
          continue;
        }

        if (needsPerspectiveQuad && !instance.quad && !instance.polygon?.length) {
          continue;
        }

        if (!needsPerspectiveQuad && !instance.bbox && !instance.quad && !instance.polygon?.length) {
          continue;
        }

        const layerOpacity = this.surfacePreviewOpacity(
          assignment.surface,
          Number(assignment.settings.opacity ?? this.settings.opacity ?? 0.82)
        );

        const surfaceSize = this.previewSurfaceSizeForInstance(assignment.surface, instance);

        layers.push({
          id: `${assignment.surface}:${instance.id}`,
          surface: assignment.surface,
          instanceId: instance.id,
          label: instance.label || null,
          wallRole: instance.wallRole || null,
          textureUrl,
          maskUrl: instance.maskUrl,
          maskFlipY: this.shouldFlipMaskY(instance.maskUrl, assignment.surface),
          sliceUrl: instance.sliceUrl,
          lightTextureUrl: null,
          perspectivePoints: instance.quad,
          polygon: instance.polygon || null,
          opacity: layerOpacity,
          rotationAngle: Number(assignment.settings.rotationAngle ?? 0),
          textureScale: Number(assignment.settings.textureScale ?? 1),
          textureOffsetX: Number(assignment.settings.textureOffsetX ?? 0),
          textureOffsetY: Number(assignment.settings.textureOffsetY ?? 0),
          pattern: this.isKisiOnlySurface(assignment.surface) ? 'straight' : String(assignment.settings.pattern || 'grid'),
          plankWidthM: Number(assignment.settings.plankWidthM || this.settings.plankWidthM || 0.1524),
          plankLengthM: Number(assignment.settings.plankLengthM || this.settings.plankLengthM || 0.9144),
          groutWidthM: Number(assignment.settings.groutWidthM || this.settings.groutWidthM || 0.002),
          groutEnabled: !!assignment.settings.groutEnabled,
          groutSizeMm: Number(assignment.settings.groutSizeMm || (assignment.settings.groutWidthM || 0) * 1000 || 0),
          groutColor: assignment.settings.groutColor || this.settings.groutColor || assignment.product.groutColor || '#9E9E9E',
          roomWidthM: Number(assignment.settings.roomWidthM || this.settings.roomWidthM || 4),
          roomDepthM: Number(assignment.settings.roomDepthM || this.settings.roomDepthM || 5),
          surfaceWidthMm: surfaceSize.widthMm,
          surfaceHeightMm: surfaceSize.heightMm,
          surfaceLengthMm: surfaceSize.lengthMm,
          surfaceDepthMm: surfaceSize.depthMm,
          productWidthMm: Number(assignment.product.width || 0) || null,
          productHeightMm: Number(assignment.product.height || 0) || null,
          pixelsPerMeter: Number(assignment.settings.pixelsPerMeter || this.settings.pixelsPerMeter || 900),
          bbox: instance.bbox || null,
        });
      }
    }

    this.previewLayers = layers;
  }


  private shouldFlipMaskY(maskUrl: string | null | undefined, surface: TargetSurface): boolean {
    const url = String(maskUrl || '').toLowerCase();

    if (url.includes('/uploads/room-templates/')) {
      return true;
    }

    // Asset runtime FastAPI lama tetap ikut behaviour sebelumnya.
    return true;
  }

  private previewSurfaceSizeForInstance(
    surface: TargetSurface,
    instance: PreparedSurfaceInstanceView
  ): { widthMm: number | null; heightMm: number | null; lengthMm: number | null; depthMm: number | null } {
    const roomWidthMm = this.resolveRoomDimensionMm('width', 3500);
    const roomDepthMm = this.resolveRoomDimensionMm('depth', 4000);
    const roomHeightMm = Number(instance.estimatedSize?.room_height_mm || instance.estimatedSize?.height_mm || 2950);

    if (surface === 'wall') {
      return {
        widthMm: instance.estimatedSize?.width_mm || roomWidthMm,
        heightMm: roomHeightMm,
        lengthMm: null,
        depthMm: null
      };
    }

    if (surface === 'floor' || surface === 'ceiling') {
      return {
        widthMm: roomWidthMm,
        heightMm: null,
        lengthMm: roomDepthMm,
        depthMm: roomDepthMm
      };
    }

    return {
      widthMm: instance.estimatedSize?.width_mm || null,
      heightMm: instance.estimatedSize?.height_mm || null,
      lengthMm: instance.estimatedSize?.length_mm || null,
      depthMm: instance.estimatedSize?.depth_mm || null
    };
  }

  private resolveRoomDimensionMm(axis: 'width' | 'depth', fallbackMm: number): number {
    const fromSettingsM = axis === 'width'
      ? Number(this.settings.roomWidthM || 0)
      : Number(this.settings.roomDepthM || 0);

    return Math.round(this.clampNumber(fromSettingsM || fallbackMm / 1000, 0.5, 40) * 1000);
  }

  private surfacePreviewOpacity(surface: TargetSurface, opacity: number): number {
    return 1;
  }


  private getOrderedAssignments(): SurfaceAssignment[] {
    const order = new Map(this.surfaceOrder.map((surface, index) => [surface, index]));

    return Object.entries(this.surfaceAssignments)
      .map(([key, assignment]) => ({ key, assignment }))
      .filter((item): item is { key: string; assignment: SurfaceAssignment } => !!item.assignment)
      .sort((a, b) => {
        const surfaceDelta = (order.get(a.assignment.surface) ?? 99) - (order.get(b.assignment.surface) ?? 99);
        return surfaceDelta !== 0 ? surfaceDelta : a.key.localeCompare(b.key);
      })
      .map((item) => item.assignment);
  }

  private async textureFileForAssignment(assignment: SurfaceAssignment): Promise<File> {
    if (assignment.customTextureFile) {
      return assignment.customTextureFile;
    }

    try {
      return await firstValueFrom(
        this.api.fetchAssetAsFile(assignment.product.textureUrl, `${assignment.product.sku || assignment.surface}.jpg`)
      );
    } catch (error) {
      console.warn('Texture produk gagal dimuat, pakai fallback dulu:', assignment.product.textureUrl, error);
      return firstValueFrom(
        this.api.fetchAssetAsFile(this.fallbackProductImageUrl, `fallback-${assignment.product.sku || assignment.surface}.jpg`)
      );
    }
  }

  private findFirstProductForSurface(surface: TargetSurface): ProductTexture | null {
    return this.compatibleProductsForSurface(surface)[0] ?? null;
  }

  private compatibleProductsForSurface(surface: TargetSurface): ProductTexture[] {
    const compatible = this.products.filter((product) => this.isProductCompatibleWithSurface(product, surface));
    return compatible.length > 0 ? compatible : this.products;
  }

  private isProductCompatibleWithSurface(product: ProductTexture, surface: TargetSurface): boolean {
    const category = String(product.category || '').toLowerCase();
    const targetClass = String(product.targetClass || '').toLowerCase();

    if (surface === 'floor') {
      return (
        targetClass === 'floor' ||
        category.includes('floor') ||
        category.includes('lantai')
      );
    }

    if (surface === 'wall') {
      return (
        targetClass === 'wall' ||
        category.includes('wall') ||
        category.includes('dinding')
      );
    }

    if (surface === 'ceiling') {
      return (
        targetClass === 'ceiling' ||
        category.includes('ceiling') ||
        category.includes('plafon') ||
        category.includes('plafond')
      );
    }

    return false;
  }

  private shouldUsePerspectiveProjection(surface: TargetSurface): boolean {
    return surface === 'floor' || surface === 'countertop' || surface === 'ceiling';
  }

  private shouldSkipPreviewInstance(surface: TargetSurface, instance: PreparedSurfaceInstanceView): boolean {
    if (surface !== 'wall') {
      return false;
    }

    return false;
  }

  private normalizeSurfaceBBox(raw: any): SurfaceBBox | null {
    if (!raw || typeof raw !== 'object') {
      return null;
    }

    const x1 = Number(raw.x1 ?? raw.left ?? raw.x ?? 0);
    const y1 = Number(raw.y1 ?? raw.top ?? raw.y ?? 0);
    const x2 = Number.isFinite(Number(raw.x2 ?? raw.right))
      ? Number(raw.x2 ?? raw.right)
      : x1 + Number(raw.width ?? 0);
    const y2 = Number.isFinite(Number(raw.y2 ?? raw.bottom))
      ? Number(raw.y2 ?? raw.bottom)
      : y1 + Number(raw.height ?? 0);

    if (![x1, y1, x2, y2].every(Number.isFinite)) {
      return null;
    }

    return {
      x1: Math.min(x1, x2),
      y1: Math.min(y1, y2),
      x2: Math.max(x1, x2),
      y2: Math.max(y1, y2),
      width: Math.abs(x2 - x1),
      height: Math.abs(y2 - y1),
      x: Number(raw.x ?? (x1 + x2) / 2),
      y: Number(raw.y ?? (y1 + y2) / 2),
    };
  }

  private clampNumber(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
  }

  private formatMm(value: unknown): string {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) && numberValue > 0 ? String(Math.round(numberValue)) : '-';
  }

  private formatError(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const backendError = error.error?.error || error.error?.message;
      const yoloStatus = error.error?.yolo_status ? ` YOLO: ${error.error.yolo_status}.` : '';
      return backendError ? `${backendError}.${yoloStatus}` : `HTTP ${error.status}: ${error.message}`;
    }

    if (error instanceof Error) {
      return error.message;
    }

    return 'Terjadi error yang tidak diketahui.';
  }
}
